﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type AssignmentRow, type OwnerRow, type ResolutionRow, type UnitRow } from "../app/api";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose } from "../hooks/useActionMenuAutoClose";
import { FACILIOO_UNIT_TYPE_VALUES, formatUnitTypeLabel } from "../shared/unitTypes";
import { getAnredeDefinitionsFromSettings, getTitelDefinitionsFromSettings } from "../shared/ownerNaming";

const hasAnyOwnerName = (name1: string, name2: string, name3: string) => !!name1.trim() || !!name2.trim() || !!name3.trim();

const unitListIcons = {
  search: (
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="9" cy="9" r="5.8" stroke="currentColor" strokeWidth="1.8" />
      <path d="m13.2 13.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  reset: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M5.2 6.3V3.9H2.8M4 4a6.4 6.4 0 1 1-1.4 7.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  object: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M6.2 5.2h7.6v9.6H6.2z" stroke="currentColor" strokeWidth="1.7" />
      <path d="M6.2 8.4h7.6M6.2 11.6h7.6M9.4 5.2v9.6M12.6 5.2v9.6" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
  entrance: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M4.2 15.7h11.6M6 15.7V5.2h8v10.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 8h4M8 11h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  location: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M10 17s5.2-4.7 5.2-9a5.2 5.2 0 0 0-10.4 0c0 4.3 5.2 9 5.2 9Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="10" cy="8" r="1.8" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  type: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M4.3 5.5h11.4M4.3 10h11.4M4.3 14.5h11.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M7.2 3.8v12.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  votes: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M4.1 15.9h11.8M5.6 15.9V9.4M10 15.9V6.7M14.4 15.9V4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  mea: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M10 3.7 15.3 6.8v6.4L10 16.3l-5.3-3.1V6.8L10 3.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M10 3.7v12.6M4.7 6.8 10 10l5.3-3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  status: (
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
      <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
    </svg>
  ),
  actions: (
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
      <circle cx="10" cy="10" r="1.4" fill="currentColor" />
      <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
    </svg>
  ),
  ownerId: (
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="7.4" r="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M5.2 15.9a4.8 4.8 0 0 1 9.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
};

const plainUnitHeader = (label: ReactNode, icon: ReactNode) => (
  <span className="resolutions-v2-th-plain">
    <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
    <span className="resolutions-v2-th-label">{label}</span>
  </span>
);

type TabKey = "uebersicht" | "einheiten" | "eigentuemer" | "zuordnungen" | "umlaufbeschluesse" | "importe" | "protokoll";
type StatusOption = "" | "aktiv" | "inaktiv";
type OwnerStatusFilterOption = "" | "aktiv" | "inaktiv" | "keine_zuordnung";
type AssignmentRecipientFilterOption = "" | "owner" | "authorized";
type UnitTypeOption = "" | string;
type UnitSortField = "objektnummer" | "einheitsnummer" | "bezeichnung";
type ResolutionStatusFilter = "" | "entwurf" | "aktiv" | "abgeschlossen";
type ResolutionResultFilter = "" | "angenommen" | "abgelehnt";
type ResolutionSortField = "beschlussnummer" | "titel" | "status" | "startdatum" | "endedatum" | "ruecklaufquote";
type Stimmrechtsart = "kopf" | "stimmrecht" | "mea";
type QuorumArt = "einfache_mehrheit" | "qualifizierte_mehrheit" | "allstimmig";
type TriggerNotificationChannel = "email" | "facilioo";

type DeleteConfirmState = { id: string; label: string; code: string; input: string[]; error: string } | null;
type StopConfirmState = { id: string; label: string; code: string; input: string[]; error: string } | null;


type ResolutionForm = {
  objectId: string;
  beschlussnummer: string;
  titel: string;
  startdatum: string;
  endedatum: string;
  stimmrechtsart: Stimmrechtsart;
  quorum: QuorumArt;
  vorbeschluss_top: string;
  vorbeschluss_wev_datum: string;
  beschlusstext: string;
  facilioo_vorgangsnummer: string;
  dispatch_automation_enabled: boolean;
  trigger_notifications_enabled: boolean;
  trigger_notifications_channel: TriggerNotificationChannel;
  trigger_notifications_events: Record<string, boolean>;
  stimmberechtigte_einheiten: string[];
};

const TRIGGER_NOTIFICATION_EVENT_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "resolution_started", label: "Info über Start des Beschlusses" },
  { key: "invitation_sent_success", label: "Info über erfolgreichen Versand der Einladung" },
  { key: "email_bounce_after_retries", label: "Info über E-Mail Rückläufer nach fehlgeschlagenen Retries" },
  { key: "analog_postal_action_required", label: "Hinweis bei analogen Eigentümern (postalischer Versand erforderlich)" },
  { key: "vote_with_comment", label: "Info bei Stimmabgabe mit Kommentar (inkl. Kommentarinhalt)" },
  { key: "reminder_sent", label: "Info über Versand von Erinnerungsschreiben (1/2) inkl. Empfänger" },
  { key: "each_vote_submitted", label: "Info über jede Stimmabgabe" },
  { key: "preliminary_result_locked", label: "Info bei vorläufigem Endergebnis (Ergebnis kann nicht mehr kippen)" },
  { key: "resolution_finished_with_result", label: "Info über Ende des Beschlusses inkl. Ergebnis" }
];

const defaultTriggerNotificationEvents = () =>
  Object.fromEntries(TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => [eventOption.key, false])) as Record<string, boolean>;

const allTriggerNotificationEvents = () =>
  Object.fromEntries(TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => [eventOption.key, true])) as Record<string, boolean>;

const mergeSelectedFiles = (previous: File[], incoming: File[], replace = false) => {
  const base = replace ? [] : previous;
  const deduped = new Map<string, File>();
  for (const file of base) {
    deduped.set(`${file.name}__${file.size}__${file.lastModified}`, file);
  }
  for (const file of incoming) {
    deduped.set(`${file.name}__${file.size}__${file.lastModified}`, file);
  }
  return Array.from(deduped.values());
};

const normalizeTriggerNotificationEvents = (value?: Record<string, boolean>) => {
  const base = defaultTriggerNotificationEvents();
  if (!value) return base;
  for (const [key, raw] of Object.entries(value)) {
    if (!(key in base)) continue;
    base[key] = Boolean(raw);
  }
  return base;
};

type UnitSelectionRow = {
  unitId: string;
  einheitsnummer: string;
  bezeichnung: string;
  einheitstyp: string;
  aufgang: string;
  eigentuemerInfo: string;
};

const getTodayDateKey = () => new Date().toISOString().slice(0, 10);

const getDateFieldErrors = (startdatum?: string, endedatum?: string): { startError: string | null; endError: string | null } => {
  const today = getTodayDateKey();
  const startError = startdatum && startdatum <= today ? "Startdatum muss in der Zukunft liegen" : null;
  const endError = startdatum && endedatum && endedatum <= startdatum ? "Enddatum muss nach dem Startdatum liegen" : null;
  return { startError, endError };
};

const getDateRangeError = (startdatum?: string, endedatum?: string): string | null => {
  const errors = getDateFieldErrors(startdatum, endedatum);
  return errors.startError ?? errors.endError;
};

const formatDateDE = (value?: string) => {
  if (!value) return "-";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
};

const formatDateTimeDE = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(parsed);
};

const buildOwnerValidityTooltip = (owner: OwnerRow) =>
  `EGT ab: ${formatDateDE(owner.gueltig_ab)} | EGT bis: ${formatDateDE(owner.gueltig_bis)}`;

const buildAssignmentValidityTooltip = (assignment: AssignmentRow) =>
  `EGT ab: ${formatDateDE(assignment.gueltig_ab)} | EGT bis: ${formatDateDE(assignment.gueltig_bis)}`;

const formatOwnerStatusLabel = (status: OwnerRow["status"]) =>
  status === "keine_zuordnung" ? "keine Zuordnung" : status;

const formatOwnerDisplayName = (owner?: Partial<OwnerRow> | null) => {
  if (!owner) return "-";
  const parts = [owner.anrede, owner.vorname, owner.name_1, owner.name_2, owner.name_3]
    .map((part) => part?.trim())
    .filter(Boolean);
  return parts.length ? parts.join(" ") : "-";
};

const formatOwnerLabelFallback = (value?: string) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  return raw.replace(/^\s*\d+\s*-\s*/u, "").trim() || raw;
};
const parseFacilioo = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (!/^\d{1,10}$/.test(trimmed)) throw new Error("Facilioo Vorgangsnummer muss 1 bis 10-stellig numerisch sein");
  return Number(trimmed);
};

const normalizeValidationErrorMessage = (error: unknown) => {
  const fallback = "Facilioo Vorgang konnte nicht validiert werden.";
  const raw = error instanceof Error ? error.message : fallback;
  return String(raw).replace(/^API\s+\d+\s*:?\s*/i, "").trim() || fallback;
};

const formatResolutionStatus = (status: ResolutionRow["status"]) => {
  if (status === "aktiv") return "läuft";
  if (status === "abgeschlossen") return "beendet";
  return "Entwurf";
};

const formatStimmrechtsart = (value: Stimmrechtsart) => {
  if (value === "kopf") return "Köpfe";
  if (value === "mea") return "MEA";
  return "Stimmrecht";
};

const hasConfiguredStimmrechte = (units: UnitRow[]) =>
  units.some((unit) => Number(unit.stimmrecht ?? 0) > 0);

const resolveDefaultStimmrechtsart = (objectDefault: Stimmrechtsart | undefined, hasStimmrechte: boolean): Stimmrechtsart => {
  if (objectDefault === "stimmrecht" && !hasStimmrechte) return "kopf";
  return objectDefault ?? (hasStimmrechte ? "stimmrecht" : "kopf");
};

const formatQuorum = (value: QuorumArt) => {
  if (value === "qualifizierte_mehrheit") return "Qualifizierte Mehrheit (> 2/3 abgegeben)";
  if (value === "allstimmig") return "Allstimmig (100% Ja aller Stimmen)";
  return "Einfache Mehrheit (> 50% abgegeben)";
};

const formatSelectionUnitNumber = (value: string) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  return /^\d+$/.test(raw) ? raw.padStart(4, "0") : raw;
};

const isVorbeschlussRequired = (quorum: QuorumArt) => quorum !== "allstimmig";

const getResolutionOutcome = (resolution: ResolutionRow): { label: string; tone?: "success" | "danger" } => {
  if (resolution.status !== "abgeschlossen") return { label: "-" };
  return resolution.quorum_erfuellt ? { label: "Angenommen", tone: "success" } : { label: "Abgelehnt", tone: "danger" };
};

const getResolutionOverviewOutcome = (resolution: ResolutionRow): { label: string; tone?: "success" | "danger" | "neutral" } => {
  if (resolution.status === "abgeschlossen") return getResolutionOutcome(resolution);
  if (resolution.status === "aktiv") {
    const totalVotes = resolution.anzahl_empfaenger_gesamt ?? resolution.stimmberechtigte_einheiten.length;
    return { label: `${resolution.abgegebene_stimmen} / ${totalVotes} abgestimmt`, tone: "neutral" };
  }
  return { label: "Noch nicht gestartet", tone: "neutral" };
};

const buildUnitSelectionRows = (units: UnitRow[], assignments: AssignmentRow[], owners: OwnerRow[]): UnitSelectionRow[] => {
  const ownerMap = new Map(owners.map((o) => [o.id, formatOwnerDisplayName(o)]));
  return units
    .filter((u) => u.status === "aktiv")
    .map((unit) => {
      const ownerLabels = assignments
        .filter((a) => a.unitId === unit.id && a.status === "aktiv")
        .map((a) => ownerMap.get(a.ownerId) ?? formatOwnerLabelFallback(a.eigentuemername))
        .filter(Boolean);
      return {
        unitId: unit.id,
        einheitsnummer: unit.einheitsnummer,
        bezeichnung: unit.bezeichnung || "-",
        einheitstyp: unit.einheitstyp || "-",
        aufgang: unit.aufgangName || "-",
        eigentuemerInfo: Array.from(new Set(ownerLabels)).join(" | ") || "-"
      };
    })
    .sort((a, b) => a.einheitsnummer.localeCompare(b.einheitsnummer));
};

type ObjectDetailPageProps = {
  canManageMasterDataActions?: boolean;
  canCreateResolutions?: boolean;
};

type ObjectDetailIconName =
  | "building"
  | "resolution"
  | "home"
  | "owners"
  | "assignments"
  | "reachability"
  | "hash"
  | "pin"
  | "calendar"
  | "refresh"
  | "user"
  | "info"
  | "edit"
  | "chevron"
  | "plus";

const ObjectDetailIcon = ({ name }: { name: ObjectDetailIconName }) => {
  if (name === "building") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4.9 8.2h14.2v10.9H4.9z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.1 4.9h7.8v3.3H8.1zM8.2 12h2.6M13.2 12h2.6M8.2 15.4h2.6M13.2 15.4h2.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "resolution") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7.1 4.6h6.6l3.2 3.2v11.6H7.1z" stroke="currentColor" strokeWidth="1.85" strokeLinejoin="round" />
        <path d="M13.7 4.8v3.1h3" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m9.4 14.1 2 2 4.2-5" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="17.7" cy="17.8" r="2.1" fill="#fff" stroke="currentColor" strokeWidth="1.85" />
      </svg>
    );
  }
  if (name === "home") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5.1" y="4.8" width="13.8" height="14.8" rx="2.1" stroke="currentColor" strokeWidth="1.85" />
        <path d="M8.3 9h2.6M13.1 9h2.6M8.3 13.2h2.6M13.1 13.2h2.6" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" />
        <path d="M9.2 19.6v-2.9h5.6v2.9" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "owners") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="9.1" cy="9.1" r="2.5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="15.8" cy="8.4" r="2.1" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.7 18.6a5 5 0 0 1 8.8 0M12.2 18.6a4.2 4.2 0 0 1 7.1 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "assignments") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="6.2" cy="7.2" r="2.1" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.8" cy="16.8" r="2.1" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.8" cy="7.2" r="2.1" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.4 7.2h7.2M7.9 8.8l7.4 6.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "reachability") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="5.4" y="2.8" width="9.2" height="14.4" rx="1.9" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8.1 5.8h3.8M7.4 14.1l5.2-5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "pin") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 21s6-5.7 6-10.5a6 6 0 1 0-12 0C6 15.3 12 21 12 21Z" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="10.5" r="2" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  if (name === "calendar") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4.8" y="6.5" width="14.4" height="12.2" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.5 4.8v3M15.5 4.8v3M5.2 10.2h13.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "refresh") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M18.5 9.5a6.4 6.4 0 0 0-11.1-2L5.8 9.2M5.5 14.5a6.4 6.4 0 0 0 11.1 2l1.6-1.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.7 5.5v3.8h3.8M18.3 18.5v-3.8h-3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "user") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8.8" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M6.4 19.1a5.8 5.8 0 0 1 11.2 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "info") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.9" />
        <path d="M12 10.8v5.2M12 7.8h.01" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "edit") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m5.6 16.9-.7 2.9 2.9-.7 9.7-9.7-2.2-2.2-9.7 9.7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="m14.4 8.1 2.2 2.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "chevron") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m8 10 4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "plus") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 5.5v13M5.5 12h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12h14M12 5v14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
};

export const ObjectDetailPage = ({ canManageMasterDataActions = false, canCreateResolutions = false }: ObjectDetailPageProps) => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabKey>("uebersicht");
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState("");
  const [actionError, setActionError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>(null);
  const [stopConfirm, setStopConfirm] = useState<StopConfirmState>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);

  const [unitPage, setUnitPage] = useState(1);
  const [unitPageSize, setUnitPageSize] = useState(10);
  const [unitOpenActionMenuId, setUnitOpenActionMenuId] = useState<string | null>(null);
  const [unitSearch, setUnitSearch] = useState("");
  const [unitAufgangFilter, setUnitAufgangFilter] = useState("");
  const [unitStatus, setUnitStatus] = useState<StatusOption>("aktiv");
  const [unitTypeFilter, setUnitTypeFilter] = useState<UnitTypeOption>("");
  const [unitSortBy, setUnitSortBy] = useState<UnitSortField>("einheitsnummer");
  const [unitSortDir, setUnitSortDir] = useState<"asc" | "desc">("asc");
  const [ownerPage, setOwnerPage] = useState(1);
  const [ownerPageSize, setOwnerPageSize] = useState(10);
  const [ownerOpenActionMenuId, setOwnerOpenActionMenuId] = useState<string | null>(null);
  const [ownerSearchFilter, setOwnerSearchFilter] = useState("");
  const [ownerReachabilityFilter, setOwnerReachabilityFilter] = useState<"" | "digital" | "analog">("");
  const [ownerStatusFilter, setOwnerStatusFilter] = useState<OwnerStatusFilterOption>("aktiv");
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [assignmentPageSize, setAssignmentPageSize] = useState(10);
  const [assignmentOpenActionMenuId, setAssignmentOpenActionMenuId] = useState<string | null>(null);
  const [assignmentRecipientFilter, setAssignmentRecipientFilter] = useState<AssignmentRecipientFilterOption>("");
  const [resolutionPage, setResolutionPage] = useState(1);
  const [resolutionPageSize, setResolutionPageSize] = useState(10);
  const [resolutionOpenActionMenuId, setResolutionOpenActionMenuId] = useState<string | null>(null);
  const [resolutionSearch, setResolutionSearch] = useState("");
  const [resolutionStatusFilter, setResolutionStatusFilter] = useState<ResolutionStatusFilter>("");
  const [resolutionResultFilter, setResolutionResultFilter] = useState<ResolutionResultFilter>("");
  const [resolutionSortBy, setResolutionSortBy] = useState<ResolutionSortField>("beschlussnummer");
  const [resolutionSortDir, setResolutionSortDir] = useState<"asc" | "desc">("asc");
  const [importPage, setImportPage] = useState(1);
  const [auditPage, setAuditPage] = useState(1);

  const [showCreateUnit, setShowCreateUnit] = useState(false);
  const [showCreateOwner, setShowCreateOwner] = useState(false);
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [showCreateResolution, setShowCreateResolution] = useState(false);

  const [unitForm, setUnitForm] = useState({ einheitsnummer: "", bezeichnung: "", einheitstyp: "" as UnitTypeOption, stimmrecht: "", mea: "", status: "" as StatusOption });
  const [editUnitForm, setEditUnitForm] = useState<null | { id: string; einheitsnummer: string; bezeichnung: string; einheitstyp: string; stimmrecht: string; mea: string; status: "aktiv" | "inaktiv" }>(null);

  const [ownerForm, setOwnerForm] = useState({ eigentuemer_id: "", anrede: "", titel: "", vorname: "", name_1: "", name_2: "", name_3: "", strasse: "", plz: "", ort: "", land_code: "", land: "", email: "", telefon: "", status: "" as StatusOption });
  const [editOwnerForm, setEditOwnerForm] = useState<null | { id: string; eigentuemer_id: string; anrede: string; titel: string; vorname: string; name_1: string; name_2: string; name_3: string; strasse: string; plz: string; ort: string; land_code: string; land: string; email: string; telefon: string; status: "aktiv" | "inaktiv" }>(null);

  const [assignmentForm, setAssignmentForm] = useState({ unitId: "", ownerId: "", status: "" as StatusOption });
  const [editAssignmentForm, setEditAssignmentForm] = useState<null | { id: string; unitId: string; ownerId: string; status: "aktiv" | "inaktiv" }>(null);
  const [assignmentOwnerSearch, setAssignmentOwnerSearch] = useState("");
  const [editAssignmentOwnerSearch, setEditAssignmentOwnerSearch] = useState("");
  const [assignmentOnlyUnassigned, setAssignmentOnlyUnassigned] = useState(false);
  const [editAssignmentOnlyUnassigned, setEditAssignmentOnlyUnassigned] = useState(false);

  const [resolutionForm, setResolutionForm] = useState<ResolutionForm>({
    objectId: id,
    beschlussnummer: "",
    titel: "",
    startdatum: "",
    endedatum: "",
    stimmrechtsart: "kopf",
    quorum: "allstimmig",
    vorbeschluss_top: "",
    vorbeschluss_wev_datum: "",
    beschlusstext: "",
    facilioo_vorgangsnummer: "",
    dispatch_automation_enabled: false,
    trigger_notifications_enabled: false,
    trigger_notifications_channel: "facilioo",
    trigger_notifications_events: defaultTriggerNotificationEvents(),
    stimmberechtigte_einheiten: []
  });
  const [editResolutionForm, setEditResolutionForm] = useState<(ResolutionForm & { id: string }) | null>(null);
  const [resolutionOwnerSearch, setResolutionOwnerSearch] = useState("");
  const [resolutionUnitTypeFilter, setResolutionUnitTypeFilter] = useState("");
  const [resolutionEntranceFilter, setResolutionEntranceFilter] = useState("");
  const [createResolutionDocuments, setCreateResolutionDocuments] = useState<File[]>([]);
  const [createResolutionDocumentsDropActive, setCreateResolutionDocumentsDropActive] = useState(false);
  const createResolutionDocumentsInputRef = useRef<HTMLInputElement | null>(null);
  const [createFaciliooValidationPending, setCreateFaciliooValidationPending] = useState(false);
  const [createFaciliooValidationError, setCreateFaciliooValidationError] = useState("");
  const [createFaciliooProcessTitle, setCreateFaciliooProcessTitle] = useState("");
  const [editResolutionOwnerSearch, setEditResolutionOwnerSearch] = useState("");
  const [editResolutionUnitTypeFilter, setEditResolutionUnitTypeFilter] = useState("");
  const [editResolutionEntranceFilter, setEditResolutionEntranceFilter] = useState("");
  const [editFaciliooValidationPending, setEditFaciliooValidationPending] = useState(false);
  const [editFaciliooValidationError, setEditFaciliooValidationError] = useState("");
  const [editFaciliooProcessTitle, setEditFaciliooProcessTitle] = useState("");
  const [initSelectionObjectId, setInitSelectionObjectId] = useState("");
  const [initEditSelectionObjectId, setInitEditSelectionObjectId] = useState("");

  useEffect(() => {
    const hasOpenMenu = !!unitOpenActionMenuId || !!ownerOpenActionMenuId || !!assignmentOpenActionMenuId || !!resolutionOpenActionMenuId;
    if (!hasOpenMenu) return;
    const closeMenus = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setUnitOpenActionMenuId(null);
      setOwnerOpenActionMenuId(null);
      setAssignmentOpenActionMenuId(null);
      setResolutionOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenus();
    };
    window.addEventListener("mousedown", closeMenus);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenus);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [unitOpenActionMenuId, ownerOpenActionMenuId, assignmentOpenActionMenuId, resolutionOpenActionMenuId]);

  useEffect(() => {
    const hasOpenMenu = !!unitOpenActionMenuId || !!ownerOpenActionMenuId || !!assignmentOpenActionMenuId || !!resolutionOpenActionMenuId;
    if (!hasOpenMenu) return;
    const closeMenus = () => {
      setUnitOpenActionMenuId(null);
      setOwnerOpenActionMenuId(null);
      setAssignmentOpenActionMenuId(null);
      setResolutionOpenActionMenuId(null);
    };
    window.addEventListener("resize", closeMenus);
    window.addEventListener("scroll", closeMenus, true);
    return () => {
      window.removeEventListener("resize", closeMenus);
      window.removeEventListener("scroll", closeMenus, true);
    };
  }, [unitOpenActionMenuId, ownerOpenActionMenuId, assignmentOpenActionMenuId, resolutionOpenActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!unitOpenActionMenuId || !!ownerOpenActionMenuId || !!assignmentOpenActionMenuId || !!resolutionOpenActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => {
      setUnitOpenActionMenuId(null);
      setOwnerOpenActionMenuId(null);
      setAssignmentOpenActionMenuId(null);
      setResolutionOpenActionMenuId(null);
    },
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!unitOpenActionMenuId && !ownerOpenActionMenuId && !assignmentOpenActionMenuId && !resolutionOpenActionMenuId) {
      setActionMenuPosition(null);
      setIsActionMenuHovered(false);
    }
  }, [unitOpenActionMenuId, ownerOpenActionMenuId, assignmentOpenActionMenuId, resolutionOpenActionMenuId]);

  const closeAllActionMenus = () => {
    setUnitOpenActionMenuId(null);
    setOwnerOpenActionMenuId(null);
    setAssignmentOpenActionMenuId(null);
    setResolutionOpenActionMenuId(null);
  };

  const openDetailActionMenu = (
    menuId: string,
    trigger: HTMLElement,
    setOpenMenuId: (next: string | null) => void,
    menuHeight = 96
  ) => {
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 170;
    const margin = 8;
    const left = Math.min(Math.max(margin, rect.right - menuWidth), Math.max(margin, window.innerWidth - menuWidth - margin));
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - menuHeight - 6;
    const top = belowTop + menuHeight <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
    closeAllActionMenus();
    setActionMenuPosition({ left, top });
    setOpenMenuId(menuId);
  };

  const mergeCreateResolutionDocuments = (incoming: File[], replace = false) => {
    setCreateResolutionDocuments((prev) => mergeSelectedFiles(prev, incoming, replace));
  };

  const onCreateResolutionDocumentsDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setCreateResolutionDocumentsDropActive(false);
    mergeCreateResolutionDocuments(Array.from(event.dataTransfer.files ?? []), false);
  };

  const isUnitsTab = tab === "einheiten";
  const isOwnersTab = tab === "eigentuemer";
  const isAssignmentsTab = tab === "zuordnungen";
  const isResolutionsTab = tab === "umlaufbeschluesse";
  const isOverviewTab = tab === "uebersicht";
  const isImportTab = tab === "importe";
  const isAuditTab = tab === "protokoll";
  const needsResolutionSelectionData = isResolutionsTab || showCreateResolution || !!editResolutionForm;
  const needsOwnerOverviewAssignmentData = isOverviewTab || needsResolutionSelectionData;

  const { data: summary, isError: summaryError } = useQuery({
    queryKey: ["object-summary", id],
    queryFn: () => api.objectSummary(id),
    enabled: !!id,
    retry: 1,
    staleTime: 30_000
  });
  const { data: unitsData } = useQuery({
    queryKey: ["object-units", id, unitPage, unitPageSize, unitSearch, unitAufgangFilter, unitTypeFilter, unitStatus, unitSortBy, unitSortDir],
    queryFn: () => api.units({
      page: unitPage,
      pageSize: unitPageSize,
      limit: unitPageSize,
      objectId: id,
      search: unitSearch,
      aufgang: unitAufgangFilter || undefined,
      aufgangExact: !!unitAufgangFilter || undefined,
      status: unitStatus || undefined,
      einheitstyp: unitTypeFilter || undefined,
      sortBy: unitSortBy,
      sortDir: unitSortDir
    }),
    enabled: !!id && isUnitsTab
  });
  const { data: ownersData } = useQuery({
    queryKey: ["object-owners", id, ownerPage, ownerPageSize, ownerSearchFilter, ownerReachabilityFilter, ownerStatusFilter],
    queryFn: () =>
      api.owners({
        page: ownerPage,
        pageSize: ownerPageSize,
        limit: ownerPageSize,
        objectId: id,
        search: ownerSearchFilter,
        erreichbarkeit: ownerReachabilityFilter || undefined,
        status: ownerStatusFilter || undefined
      }),
    enabled: !!id && isOwnersTab
  });
  const { data: assignmentsData } = useQuery({
    queryKey: ["object-assignments", id, assignmentPage, assignmentPageSize, search, assignmentRecipientFilter],
    queryFn: () =>
      api.assignments({
        page: assignmentPage,
        pageSize: assignmentPageSize,
        limit: assignmentPageSize,
        objectId: id,
        search,
        recipientType: assignmentRecipientFilter || undefined
      }),
    enabled: !!id && isAssignmentsTab
  });
  const { data: resolutionsData } = useQuery({
    queryKey: ["object-resolutions", id, resolutionPage, resolutionPageSize, resolutionSearch, resolutionStatusFilter, resolutionResultFilter, resolutionSortBy, resolutionSortDir],
    queryFn: () =>
      api.resolutions({
        page: resolutionPage,
      pageSize: resolutionPageSize,
      limit: resolutionPageSize,
        objectId: id,
        search: resolutionSearch,
        status: resolutionStatusFilter || undefined,
        ergebnis: resolutionResultFilter || undefined,
        sortBy: resolutionSortBy,
        sortDir: resolutionSortDir
      }),
    enabled: !!id && isResolutionsTab
  });
  const { data: resolutionsOverviewData } = useQuery({
    queryKey: ["object-resolutions-overview", id],
    queryFn: () => api.resolutions({ page: 1, pageSize: 5, objectId: id, lightweight: "1" }),
    enabled: !!id && isOverviewTab
  });
  const { data: completedResolutionsOverviewData } = useQuery({
    queryKey: ["object-resolutions-overview-count", id, "abgeschlossen"],
    queryFn: () => api.resolutions({ page: 1, pageSize: 1, objectId: id, status: "abgeschlossen", lightweight: "1" }),
    enabled: !!id && isOverviewTab
  });
  const { data: draftResolutionsOverviewData } = useQuery({
    queryKey: ["object-resolutions-overview-count", id, "entwurf"],
    queryFn: () => api.resolutions({ page: 1, pageSize: 1, objectId: id, status: "entwurf", lightweight: "1" }),
    enabled: !!id && isOverviewTab
  });
  const { data: unitsOverviewData } = useQuery({
    queryKey: ["object-units-overview", id],
    queryFn: () => api.units({ page: 1, pageSize: 2000, objectId: id }),
    enabled: !!id && isUnitsTab
  });
  const { data: wohnungUnitsOverviewData } = useQuery({
    queryKey: ["object-units-overview-count", id, "wohnung"],
    queryFn: () => api.units({ page: 1, pageSize: 1, objectId: id, einheitstyp: "wohnung" }),
    enabled: !!id && isOverviewTab
  });
  const { data: stellplatzUnitsOverviewData } = useQuery({
    queryKey: ["object-units-overview-count", id, "stellplatz"],
    queryFn: () => api.units({ page: 1, pageSize: 1, objectId: id, einheitstyp: "stellplatz" }),
    enabled: !!id && isOverviewTab
  });
  const { data: sonstigeUnitsOverviewData } = useQuery({
    queryKey: ["object-units-overview-count", id, "sonstiges"],
    queryFn: () => api.units({ page: 1, pageSize: 1, objectId: id, einheitstyp: "sonstiges" }),
    enabled: !!id && isOverviewTab
  });
  const { data: ownersOverviewData } = useQuery({
    queryKey: ["object-owners-overview", id],
    queryFn: () => api.owners({ page: 1, pageSize: 2000, objectId: id }),
    enabled: !!id && isAssignmentsTab
  });
  const { data: activeResolutionsData } = useQuery({
    queryKey: ["object-active-resolutions-lock", id],
    queryFn: () => api.resolutions({ page: 1, pageSize: 1, objectId: id, status: "aktiv", lightweight: "1" }),
    enabled: !!id && (isOverviewTab || canManageMasterDataActions)
  });
  const { data: importsData } = useQuery({
    queryKey: ["object-imports", importPage],
    queryFn: () => api.imports({ page: importPage, pageSize: 25 }),
    enabled: !!id && isImportTab
  });
  const { data: auditData } = useQuery({
    queryKey: ["object-audit", id, auditPage],
    queryFn: () => api.auditLogs({ page: auditPage, pageSize: 25, search: summary?.object.objektnummer }),
    enabled: !!id && isAuditTab && !!summary?.object.objektnummer
  });
  const { data: uiSettings } = useQuery({
    queryKey: ["ui-settings-object-owner-anrede"],
    queryFn: () => api.uiSettings(),
    staleTime: 60_000
  });
  const { data: nextResolutionNumberData } = useQuery({
    queryKey: ["resolutions-next-beschlussnummer"],
    queryFn: () => api.nextResolutionNumber(),
    staleTime: 15_000
  });

  const { data: unitsForSelectionData } = useQuery({
    queryKey: ["object-resolution-units-all", id],
    queryFn: () => api.units({ page: 1, pageSize: 1200, objectId: id, status: "aktiv" }),
    enabled: !!id && needsResolutionSelectionData
  });
  const { data: ownersForSelectionData } = useQuery({
    queryKey: ["object-resolution-owners-all", id],
    queryFn: () => api.owners({ page: 1, pageSize: 2000, objectId: id, status: "aktiv" }),
    enabled: !!id && needsResolutionSelectionData
  });
  const { data: assignmentsForSelectionData } = useQuery({
    queryKey: ["object-resolution-assignments-all", id],
    queryFn: () => api.assignments({ page: 1, pageSize: 5000, objectId: id, status: "aktiv" }),
    enabled: !!id && needsOwnerOverviewAssignmentData
  });
  const needsAssignmentOwnerContext = isAssignmentsTab || showCreateAssignment || !!editAssignmentForm;
  const { data: ownerAssignmentsOverviewData } = useQuery({
    queryKey: ["owners-assignment-overview", "object-detail", id],
    queryFn: () => api.assignments({ page: 1, pageSize: 5000, objectId: id, status: "aktiv" }),
    enabled: !!id && needsAssignmentOwnerContext
  });

  const objectLocked = Number(activeResolutionsData?.meta?.total ?? activeResolutionsData?.data.length ?? 0) > 0;
  const anredeOptions = useMemo(
    () => getAnredeDefinitionsFromSettings(uiSettings?.management),
    [uiSettings?.management]
  );
  const titelOptions = useMemo(
    () => getTitelDefinitionsFromSettings(uiSettings?.management),
    [uiSettings?.management]
  );
  const unitSelectionRows = useMemo(() => buildUnitSelectionRows(unitsForSelectionData?.data ?? [], assignmentsForSelectionData?.data ?? [], ownersForSelectionData?.data ?? []), [assignmentsForSelectionData?.data, ownersForSelectionData?.data, unitsForSelectionData?.data]);
  const objectHasStimmrechte = useMemo(
    () => hasConfiguredStimmrechte(unitsForSelectionData?.data ?? []),
    [unitsForSelectionData?.data]
  );
  const objectStandardStimmrechtsart = summary?.object.standard_stimmrechtsart;
  const objectCanUseStimmrecht = objectHasStimmrechte;
  const assignmentRefsByOwner = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const assignment of ownerAssignmentsOverviewData?.data ?? []) {
      const list = map.get(assignment.ownerId) ?? [];
      list.push(`${assignment.objektnummer}/${assignment.einheitsnummer}`);
      map.set(assignment.ownerId, list);
    }
    return map;
  }, [ownerAssignmentsOverviewData?.data]);
  const filteredAssignmentOwners = useMemo(() => {
    const rows = ownersForSelectionData?.data ?? [];
    const needle = assignmentOwnerSearch.trim().toLowerCase();
    const searched = !needle
      ? rows
      : rows.filter((owner) =>
      `${owner.eigentuemer_id} ${owner.vollname} ${owner.email ?? ""}`.toLowerCase().includes(needle)
    );
    if (!assignmentOnlyUnassigned) return searched;
    return searched.filter((owner) => (assignmentRefsByOwner.get(owner.id) ?? []).length === 0);
  }, [ownersForSelectionData?.data, assignmentOwnerSearch, assignmentOnlyUnassigned, assignmentRefsByOwner]);
  const filteredEditAssignmentOwners = useMemo(() => {
    const rows = ownersForSelectionData?.data ?? [];
    const needle = editAssignmentOwnerSearch.trim().toLowerCase();
    const searched = !needle
      ? rows
      : rows.filter((owner) =>
      `${owner.eigentuemer_id} ${owner.vollname} ${owner.email ?? ""}`.toLowerCase().includes(needle)
    );
    if (!editAssignmentOnlyUnassigned) return searched;
    return searched.filter((owner) => (assignmentRefsByOwner.get(owner.id) ?? []).length === 0);
  }, [ownersForSelectionData?.data, editAssignmentOwnerSearch, editAssignmentOnlyUnassigned, assignmentRefsByOwner]);
  const ownerDisplayById = useMemo(() => {
    const map = new Map<string, string>();
    for (const owner of ownersOverviewData?.data ?? []) {
      map.set(owner.id, formatOwnerDisplayName(owner));
    }
    return map;
  }, [ownersOverviewData?.data]);
  const formatEinheitsnummer = (value: string) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return "";
    return /^\d+$/.test(trimmed) ? trimmed.padStart(4, "0") : trimmed;
  };
  const formatNumberDE = (value: number | string | null | undefined) => {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed)
      ? new Intl.NumberFormat("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(parsed)
      : "-";
  };
  const toggleUnitSort = (field: UnitSortField) => {
    if (unitSortBy === field) {
      setUnitSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setUnitSortBy(field);
    setUnitSortDir("asc");
  };
  const renderUnitSortIndicator = (field: UnitSortField) => (
    <span className="resolutions-v2-sort-indicator" aria-hidden="true">
      {unitSortBy === field ? (unitSortDir === "asc" ? "?" : "?") : "?"}
    </span>
  );
  const renderUnitSortHeader = (field: UnitSortField, label: ReactNode, icon: ReactNode) => (
    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleUnitSort(field)}>
      <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
      <span className="resolutions-v2-th-label">{label}</span>
      {renderUnitSortIndicator(field)}
    </button>
  );
  const renderAssignmentOwnerSearchList = (
    rows: OwnerRow[],
    selectedOwnerId: string,
    onSelectOwner: (ownerId: string) => void
  ) => (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-soft)" }}>Suchergebnisse</div>
      <div style={{ border: "1px solid var(--line)", borderRadius: 6, minHeight: 140, maxHeight: 240, overflowY: "auto", overflowX: "hidden", background: "#fff" }}>
        <table style={{ tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "11%" }}>Eigentümer-ID</th>
              <th style={{ width: "9%" }}>Anrede</th>
              <th style={{ width: "11%" }}>Vorname</th>
              <th style={{ width: "12%" }}>Name 1</th>
              <th style={{ width: "12%" }}>Name 2</th>
              <th style={{ width: "12%" }}>Name 3</th>
              <th style={{ width: "16%" }}>E-Mail</th>
              <th style={{ width: "17%" }}>Zuordnungen</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((owner) => {
                const selected = selectedOwnerId === owner.id;
                const refs = assignmentRefsByOwner.get(owner.id) ?? [];
                return (
                  <tr
                    key={owner.id}
                    onClick={() => onSelectOwner(owner.id)}
                    style={{ cursor: "pointer", background: selected ? "var(--surface-muted)" : undefined }}
                  >
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.eigentuemer_id}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.anrede || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.vorname || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.name_1 || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.name_2 || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.name_3 || "-"}</td>
                    <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{owner.email || "-"}</td>
                    <td style={{ color: "var(--text-soft)", whiteSpace: "normal", overflowWrap: "anywhere" }}>
                      {refs.length ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {refs.slice(0, 3).map((ref) => (
                            <span
                              key={`${owner.id}-${ref}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: 999,
                                border: "1px solid #c9d1da",
                                background: "#f2f4f7",
                                color: "#344054",
                                fontSize: 12,
                                lineHeight: 1.2
                              }}
                            >
                              {ref}
                            </span>
                          ))}
                          {refs.length > 3 ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: 999,
                                border: "1px solid #c9d1da",
                                background: "#f2f4f7",
                                color: "#344054",
                                fontSize: 12,
                                lineHeight: 1.2
                              }}
                            >
                              +{refs.length - 3}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} style={{ color: "var(--text-soft)" }}>Keine passenden Eigentümer gefunden.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  useEffect(() => {
    setUnitPage(1);
  }, [unitSearch, unitAufgangFilter, unitTypeFilter, unitStatus, unitSortBy, unitSortDir]);

  useEffect(() => {
    setOwnerPage(1);
  }, [ownerSearchFilter, ownerReachabilityFilter, ownerStatusFilter]);

  useEffect(() => {
    if (!id || !unitSelectionRows.length) return;
    if (initSelectionObjectId === id) return;
    setResolutionForm((prev) => ({ ...prev, objectId: id, stimmberechtigte_einheiten: unitSelectionRows.map((u) => u.unitId) }));
    setInitSelectionObjectId(id);
  }, [id, initSelectionObjectId, unitSelectionRows]);

  useEffect(() => {
    if (!editResolutionForm || !unitSelectionRows.length) return;
    if (initEditSelectionObjectId === editResolutionForm.objectId) return;
    const selected = editResolutionForm.stimmberechtigte_einheiten.length ? editResolutionForm.stimmberechtigte_einheiten.filter((unitId) => unitSelectionRows.some((u) => u.unitId === unitId)) : unitSelectionRows.map((u) => u.unitId);
    setEditResolutionForm({ ...editResolutionForm, stimmberechtigte_einheiten: selected });
    setInitEditSelectionObjectId(editResolutionForm.objectId);
  }, [editResolutionForm, initEditSelectionObjectId, unitSelectionRows]);

  const invalidateObjectTabs = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["object-summary", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-units", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-owners", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-assignments", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-resolutions", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-resolutions-overview", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-resolution-units-all", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-resolution-owners-all", id] }),
      queryClient.invalidateQueries({ queryKey: ["object-resolution-assignments-all", id] }),
      queryClient.invalidateQueries({ queryKey: ["active-resolutions-lock"] })
    ]);
  };

  const createUnitMutation = useMutation({
    mutationFn: () => {
      if (objectLocked) throw new Error("Objekt ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.createUnit({ objectId: id, einheitsnummer: unitForm.einheitsnummer.trim(), bezeichnung: unitForm.bezeichnung.trim(), einheitstyp: unitForm.einheitstyp, stimmrecht: Number(unitForm.stimmrecht), mea: unitForm.mea ? Number(unitForm.mea) : undefined, status: unitForm.status as "aktiv" | "inaktiv" });
    },
    onSuccess: async () => {
      setShowCreateUnit(false);
      setUnitForm({ einheitsnummer: "", bezeichnung: "", einheitstyp: "", stimmrecht: "", mea: "", status: "" });
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Einheit konnte nicht angelegt werden")
  });

  const updateUnitMutation = useMutation({
    mutationFn: () => {
      if (!editUnitForm) throw new Error("Keine Einheit gewählt");
      if (objectLocked) throw new Error("Objekt ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.updateUnit(editUnitForm.id, { einheitsnummer: editUnitForm.einheitsnummer.trim(), bezeichnung: editUnitForm.bezeichnung.trim(), einheitstyp: editUnitForm.einheitstyp, stimmrecht: Number(editUnitForm.stimmrecht), mea: editUnitForm.mea ? Number(editUnitForm.mea) : undefined, status: editUnitForm.status });
    },
    onSuccess: async () => {
      setEditUnitForm(null);
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Einheit konnte nicht bearbeitet werden")
  });

  const createOwnerMutation = useMutation({
    mutationFn: () => {
      if (objectLocked) throw new Error("Objekt ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.createOwner({ objectId: id, eigentuemer_id: ownerForm.eigentuemer_id.trim(), anrede: ownerForm.anrede.trim() || undefined, titel: ownerForm.titel.trim() || undefined, vorname: ownerForm.vorname.trim() || undefined, name_1: ownerForm.name_1.trim() || undefined, name_2: ownerForm.name_2.trim() || undefined, name_3: ownerForm.name_3.trim() || undefined, strasse: ownerForm.strasse.trim() || undefined, plz: ownerForm.plz.trim() || undefined, ort: ownerForm.ort.trim() || undefined, land_code: ownerForm.land_code.trim() || undefined, land: ownerForm.land.trim() || undefined, email: ownerForm.email.trim(), telefon: ownerForm.telefon.trim() || undefined, status: ownerForm.status as "aktiv" | "inaktiv" });
    },
    onSuccess: async () => {
      setShowCreateOwner(false);
      setOwnerForm({ eigentuemer_id: "", anrede: "", titel: "", vorname: "", name_1: "", name_2: "", name_3: "", strasse: "", plz: "", ort: "", land_code: "", land: "", email: "", telefon: "", status: "" });
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Eigentümer konnte nicht angelegt werden")
  });

  const updateOwnerMutation = useMutation({
    mutationFn: () => {
      if (!editOwnerForm) throw new Error("Kein Eigentümer gewählt");
      if (objectLocked) throw new Error("Objekt ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.updateOwner(editOwnerForm.id, { objectId: id, eigentuemer_id: editOwnerForm.eigentuemer_id.trim(), anrede: editOwnerForm.anrede.trim() || undefined, titel: editOwnerForm.titel.trim() || undefined, vorname: editOwnerForm.vorname.trim() || undefined, name_1: editOwnerForm.name_1.trim() || undefined, name_2: editOwnerForm.name_2.trim() || undefined, name_3: editOwnerForm.name_3.trim() || undefined, strasse: editOwnerForm.strasse.trim() || undefined, plz: editOwnerForm.plz.trim() || undefined, ort: editOwnerForm.ort.trim() || undefined, land_code: editOwnerForm.land_code.trim() || undefined, land: editOwnerForm.land.trim() || undefined, email: editOwnerForm.email.trim(), telefon: editOwnerForm.telefon.trim() || undefined, status: editOwnerForm.status });
    },
    onSuccess: async () => {
      setEditOwnerForm(null);
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Eigentümer konnte nicht bearbeitet werden")
  });

  const createAssignmentMutation = useMutation({
    mutationFn: () => {
      if (objectLocked) throw new Error("Objekt ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.createAssignment({ objectId: id, unitId: assignmentForm.unitId, ownerId: assignmentForm.ownerId, status: assignmentForm.status as "aktiv" | "inaktiv" });
    },
    onSuccess: async () => {
      setShowCreateAssignment(false);
      setAssignmentForm({ unitId: "", ownerId: "", status: "" });
      setAssignmentOwnerSearch("");
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Zuordnung konnte nicht angelegt werden")
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: () => {
      if (!editAssignmentForm) throw new Error("Keine Zuordnung gewählt");
      if (objectLocked) throw new Error("Objekt ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.updateAssignment(editAssignmentForm.id, { objectId: id, unitId: editAssignmentForm.unitId, ownerId: editAssignmentForm.ownerId, status: editAssignmentForm.status });
    },
    onSuccess: async () => {
      setEditAssignmentForm(null);
      setEditAssignmentOwnerSearch("");
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Zuordnung konnte nicht bearbeitet werden")
  });

  const createResolutionMutation = useMutation({
    mutationFn: async () => {
      const dateError = getDateRangeError(resolutionForm.startdatum || undefined, resolutionForm.endedatum || undefined);
      if (dateError) throw new Error(dateError);
      const hasVorbeschlussTop = !!resolutionForm.vorbeschluss_top.trim();
      const hasVorbeschlussDate = !!resolutionForm.vorbeschluss_wev_datum;
      if (hasVorbeschlussTop !== hasVorbeschlussDate) {
        throw new Error("TOP und Datum der WEV müssen gemeinsam befüllt werden.");
      }
      if (!(await validateCreateFaciliooProcess())) {
        throw new Error("Facilioo Vorgang konnte nicht validiert werden.");
      }
      if (resolutionForm.stimmberechtigte_einheiten.length === 0) throw new Error("Mindestens eine stimmberechtigte Einheit auswählen.");
      const created = await api.createResolution({ objectId: id, titel: resolutionForm.titel.trim(), startdatum: resolutionForm.startdatum || undefined, endedatum: resolutionForm.endedatum || undefined, stimmrechtsart: resolutionForm.stimmrechtsart, quorum: resolutionForm.quorum, vorbeschluss_top: resolutionForm.vorbeschluss_top.trim() || undefined, vorbeschluss_wev_datum: resolutionForm.vorbeschluss_wev_datum || undefined, stimmberechtigte_einheiten: resolutionForm.stimmberechtigte_einheiten, beschlusstext: resolutionForm.beschlusstext.trim(), facilioo_vorgangsnummer: parseFacilioo(resolutionForm.facilioo_vorgangsnummer), dispatch_automation_enabled: resolutionForm.dispatch_automation_enabled, trigger_notifications_enabled: resolutionForm.trigger_notifications_enabled, trigger_notifications_channel: resolutionForm.trigger_notifications_channel, trigger_notifications_events: normalizeTriggerNotificationEvents(resolutionForm.trigger_notifications_events) });
      if (createResolutionDocuments.length > 0) {
        await api.uploadResolutionDocuments(created.id, createResolutionDocuments);
      }
      return created;
    },
    onSuccess: async () => {
      setShowCreateResolution(false);
      setResolutionForm({ objectId: id, beschlussnummer: "", titel: "", startdatum: "", endedatum: "", stimmrechtsart: resolveDefaultStimmrechtsart(objectStandardStimmrechtsart, objectHasStimmrechte), quorum: "allstimmig", vorbeschluss_top: "", vorbeschluss_wev_datum: "", beschlusstext: "", facilioo_vorgangsnummer: "", dispatch_automation_enabled: false, trigger_notifications_enabled: false, trigger_notifications_channel: "facilioo", trigger_notifications_events: defaultTriggerNotificationEvents(), stimmberechtigte_einheiten: unitSelectionRows.map((u) => u.unitId) });
      setResolutionOwnerSearch("");
      setResolutionUnitTypeFilter("");
      setResolutionEntranceFilter("");
      setCreateResolutionDocuments([]);
      setCreateFaciliooValidationError("");
      setCreateFaciliooProcessTitle("");
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Umlaufbeschluss konnte nicht angelegt werden")
  });

  const updateResolutionMutation = useMutation({
    mutationFn: async () => {
      if (!editResolutionForm) throw new Error("Kein Beschluss gewählt");
      const dateError = getDateRangeError(editResolutionForm.startdatum || undefined, editResolutionForm.endedatum || undefined);
      if (dateError) throw new Error(dateError);
      const hasVorbeschlussTop = !!editResolutionForm.vorbeschluss_top.trim();
      const hasVorbeschlussDate = !!editResolutionForm.vorbeschluss_wev_datum;
      if (hasVorbeschlussTop !== hasVorbeschlussDate) {
        throw new Error("TOP und Datum der WEV müssen gemeinsam befüllt werden.");
      }
      if (!(await validateEditFaciliooProcess())) {
        throw new Error("Facilioo Vorgang konnte nicht validiert werden.");
      }
      if (editResolutionForm.stimmberechtigte_einheiten.length === 0) throw new Error("Mindestens eine stimmberechtigte Einheit auswählen.");
      return api.updateResolution(editResolutionForm.id, { objectId: id, titel: editResolutionForm.titel.trim(), startdatum: editResolutionForm.startdatum || undefined, endedatum: editResolutionForm.endedatum || undefined, stimmrechtsart: editResolutionForm.stimmrechtsart, quorum: editResolutionForm.quorum, vorbeschluss_top: editResolutionForm.vorbeschluss_top.trim() || undefined, vorbeschluss_wev_datum: editResolutionForm.vorbeschluss_wev_datum || undefined, stimmberechtigte_einheiten: editResolutionForm.stimmberechtigte_einheiten, beschlusstext: editResolutionForm.beschlusstext.trim(), facilioo_vorgangsnummer: parseFacilioo(editResolutionForm.facilioo_vorgangsnummer), dispatch_automation_enabled: editResolutionForm.dispatch_automation_enabled, trigger_notifications_enabled: editResolutionForm.trigger_notifications_enabled, trigger_notifications_channel: editResolutionForm.trigger_notifications_channel, trigger_notifications_events: normalizeTriggerNotificationEvents(editResolutionForm.trigger_notifications_events) });
    },
    onSuccess: async () => {
      setEditResolutionForm(null);
      setEditResolutionOwnerSearch("");
      setEditResolutionUnitTypeFilter("");
      setEditResolutionEntranceFilter("");
      setEditFaciliooValidationError("");
      setEditFaciliooProcessTitle("");
      setFormError("");
      await invalidateObjectTabs();
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Umlaufbeschluss konnte nicht bearbeitet werden")
  });

  const startResolutionMutation = useMutation({ mutationFn: (rid: string) => api.startResolution(rid), onSuccess: async () => { setActionError(""); await invalidateObjectTabs(); }, onError: (error) => setActionError(error instanceof Error ? error.message : "Start fehlgeschlagen") });
  const stopResolutionMutation = useMutation({ mutationFn: (rid: string) => api.stopResolution(rid), onSuccess: async () => { setStopConfirm(null); setActionError(""); await invalidateObjectTabs(); }, onError: (error) => setActionError(error instanceof Error ? error.message : "Stop fehlgeschlagen") });

  const resolveDelete = async () => {
    if (!deleteConfirm) return;
    const [kind, entityId] = deleteConfirm.id.split(":", 2);
    try {
      if (kind !== "resolution" && objectLocked) throw new Error("Objekt ist wegen aktivem Umlaufbeschluss gesperrt");
      if (kind === "unit") await api.deactivateUnit(entityId);
      if (kind === "owner") await api.deactivateOwner(entityId);
      if (kind === "assignment") await api.deleteAssignment(entityId);
      if (kind === "resolution") await api.deleteResolution(entityId);
      setDeleteConfirm(null);
      setActionError("");
      await invalidateObjectTabs();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Löschen fehlgeschlagen");
    }
  };

  const getFilteredResolutionUnitIds = (
    rows: UnitSelectionRow[],
    ownerSearch: string,
    unitTypeFilter: string,
    entranceFilter: string
  ) => {
    const needle = ownerSearch.trim().toLowerCase();
    return rows
      .filter((row) => {
        const searchOk =
          !needle ||
          row.eigentuemerInfo.toLowerCase().includes(needle) ||
          row.einheitsnummer.toLowerCase().includes(needle);
        const typeOk = !unitTypeFilter || row.einheitstyp === unitTypeFilter;
        const entranceOk = !entranceFilter || row.aufgang === entranceFilter;
        return searchOk && typeOk && entranceOk;
      })
      .map((row) => row.unitId);
  };

  const renderUnitSelector = (
    rows: UnitSelectionRow[],
    selectedUnitIds: string[],
    ownerSearch: string,
    setOwnerSearch: (value: string) => void,
    unitTypeFilter: string,
    setUnitTypeFilter: (value: string) => void,
    entranceFilter: string,
    setEntranceFilter: (value: string) => void,
    onToggleUnit: (unitId: string, checked: boolean) => void,
    onSelectAll: () => void,
    onDeselectAll: () => void,
    onMarkFiltered: () => void,
    onDemarkFiltered: () => void
  ) => {
    const needle = ownerSearch.trim().toLowerCase();
    const filteredRows = rows.filter((row) => {
      const unitNumberFormatted = formatSelectionUnitNumber(row.einheitsnummer).toLowerCase();
      const searchOk =
        !needle ||
        row.eigentuemerInfo.toLowerCase().includes(needle) ||
        row.einheitsnummer.toLowerCase().includes(needle) ||
        unitNumberFormatted.includes(needle);
      const typeOk = !unitTypeFilter || row.einheitstyp === unitTypeFilter;
      const entranceOk = !entranceFilter || row.aufgang === entranceFilter;
      return searchOk && typeOk && entranceOk;
    });
    const availableUnitTypes = Array.from(new Set(rows.map((row) => row.einheitstyp))).sort((a, b) => a.localeCompare(b));
    const availableEntrances = Array.from(new Set(rows.map((row) => row.aufgang))).sort((a, b) => a.localeCompare(b));
    return (
      <div className="form-field form-field-wide resolution-selection-box">
        <div className="resolution-create-v2-section-head">
          <span className="resolution-create-v2-section-icon" aria-hidden="true">
            <ObjectDetailIcon name="resolution" />
          </span>
          <label>Stimmberechtigte Eigentümer/Einheiten*</label>
        </div>
        <div className="resolutions-v2-filterbar resolution-create-v2-unit-filterbar">
          <div className="resolutions-v2-filter-fields resolution-create-v2-filter-fields">
            <label className="resolutions-v2-filter-input resolution-create-v2-filter-input">
              <span className="resolutions-v2-filter-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <circle cx="9" cy="9" r="5.8" stroke="currentColor" strokeWidth="1.8" />
                  <path d="m13.2 13.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <input placeholder="Eigentümer suchen (ID oder Name)" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} />
            </label>
          <select value={unitTypeFilter} onChange={(e) => setUnitTypeFilter(e.target.value)}>
            <option value="">Alle Einheitstypen</option>
            {availableUnitTypes.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          <select value={entranceFilter} onChange={(e) => setEntranceFilter(e.target.value)}>
            <option value="">Alle Aufgänge</option>
            {availableEntrances.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          </div>
          <div className="resolution-create-v2-filter-actions">
          <button type="button" onClick={onSelectAll}>Alle markieren</button>
          <button type="button" onClick={onDeselectAll}>Alle demarkieren</button>
          <button type="button" onClick={onMarkFiltered} disabled={!filteredRows.length}>Gefilterte markieren</button>
          <button type="button" onClick={onDemarkFiltered} disabled={!filteredRows.length}>Gefilterte demarkieren</button>
          </div>
        </div>
        <div className="resolution-create-v2-table-wrap">
          <table className="resolution-create-v2-unit-table">
            <thead><tr><th>Aktiv</th><th>Einheit</th><th>Aufgang</th><th>Lage</th><th>Einheitstyp</th><th>Eigentümer</th></tr></thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.unitId}>
                  <td><input type="checkbox" checked={selectedUnitIds.includes(row.unitId)} onChange={(e) => onToggleUnit(row.unitId, e.target.checked)} /></td>
                  <td style={{ textAlign: "center" }}>{formatSelectionUnitNumber(row.einheitsnummer)}</td>
                  <td>{row.aufgang}</td>
                  <td>{row.bezeichnung}</td>
                  <td>{row.einheitstyp}</td>
                  <td>{row.eigentuemerInfo}</td>
                </tr>
              ))}
              {!filteredRows.length ? <tr><td colSpan={6}>Keine passenden Einheiten gefunden.</td></tr> : null}
            </tbody>
          </table>
        </div>
        <div className="resolution-create-v2-selection-count">Ausgewählt: {selectedUnitIds.length} von {rows.length} Einheiten</div>
      </div>
    );
  };

  const unitsRows = unitsData?.data ?? [];
  const unitTotalRows = unitsData?.meta.total ?? 0;
  const unitTotalPages = Math.max(1, Math.ceil(unitTotalRows / unitPageSize));
  const unitPageItems = useMemo(() => {
    const start = Math.max(1, unitPage - 2);
    const end = Math.min(unitTotalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [unitPage, unitTotalPages]);
  const unitAufgangOptions = useMemo(() => {
    const source = unitsOverviewData?.data ?? [];
    return Array.from(
      new Set(
        source
          .map((u) => (u.aufgangName || u.aufgangRef || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "de"));
  }, [unitsOverviewData?.data]);
  const ownersRows = ownersData?.data ?? [];
  const assignmentsRows = assignmentsData?.data ?? [];
  const resolutionsRows = resolutionsData?.data ?? [];
  const ownerTotalRows = ownersData?.meta.total ?? 0;
  const ownerTotalPages = Math.max(1, Math.ceil(ownerTotalRows / ownerPageSize));
  const ownerPageItems = useMemo(() => {
    const start = Math.max(1, ownerPage - 2);
    const end = Math.min(ownerTotalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [ownerPage, ownerTotalPages]);
  const assignmentTotalRows = assignmentsData?.meta.total ?? 0;
  const assignmentTotalPages = Math.max(1, Math.ceil(assignmentTotalRows / assignmentPageSize));
  const assignmentPageItems = useMemo(() => {
    const start = Math.max(1, assignmentPage - 2);
    const end = Math.min(assignmentTotalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [assignmentPage, assignmentTotalPages]);
  const resolutionTotalRows = resolutionsData?.meta.total ?? 0;
  const resolutionTotalPages = Math.max(1, Math.ceil(resolutionTotalRows / resolutionPageSize));
  const resolutionPageItems = useMemo(() => {
    const start = Math.max(1, resolutionPage - 2);
    const end = Math.min(resolutionTotalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [resolutionPage, resolutionTotalPages]);
  const resolutionOverviewRows = resolutionsOverviewData?.data ?? [];
  const resolutionOverviewStats = {
    total: resolutionsOverviewData?.meta.total ?? 0,
    aktiv: activeResolutionsData?.meta.total ?? 0,
    abgeschlossen: completedResolutionsOverviewData?.meta.total ?? 0,
    entwurf: draftResolutionsOverviewData?.meta.total ?? 0
  };
  const latestResolutionOverviewRows = useMemo(() => {
    const toTime = (value?: string) => {
      if (!value) return 0;
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [...resolutionOverviewRows]
      .sort((a, b) => {
        const dateDiff = toTime(b.createdAt) - toTime(a.createdAt);
        if (dateDiff !== 0) return dateDiff;
        return b.beschlussnummer.localeCompare(a.beschlussnummer, "de", { numeric: true });
      })
      .slice(0, 5);
  }, [resolutionOverviewRows]);
  const unitsOverviewTotal = summary?.stats.units ?? 0;
  const unitTypeOverviewBadges = [
    { label: "Wohnungen", count: wohnungUnitsOverviewData?.meta.total ?? 0 },
    { label: "Stellplätze", count: stellplatzUnitsOverviewData?.meta.total ?? 0 },
    { label: "Sonstiges", count: sonstigeUnitsOverviewData?.meta.total ?? 0 }
  ].filter((item) => item.count > 0);
  const ownerOverviewStats = {
    total: summary?.stats.owners ?? 0,
    analog: summary?.stats.owners_analog ?? 0,
    digital: summary?.stats.owners_digital ?? Math.max((summary?.stats.owners ?? 0) - (summary?.stats.owners_analog ?? 0), 0)
  };
  const ownerOverviewAnalogPct = ownerOverviewStats.total > 0 ? (ownerOverviewStats.analog / ownerOverviewStats.total) * 100 : 0;
  const ownerOverviewDigitalPct = ownerOverviewStats.total > 0 ? (ownerOverviewStats.digital / ownerOverviewStats.total) * 100 : 0;
  const ownerOverviewDigitalPctLabel = ownerOverviewDigitalPct.toFixed(1).replace(".", ",");
  const ownerOverviewAnalogPctLabel = ownerOverviewAnalogPct.toFixed(1).replace(".", ",");
  const reachabilityRingPct = Math.max(0, Math.min(100, ownerOverviewDigitalPct));
  const reachabilityRingStyle = { "--reachability-pct": `${reachabilityRingPct}%` } as CSSProperties;
  const objectIsActive = summary?.object.status === "aktiv";
  const objectAddressLabel = useMemo(() => {
    const street = [summary?.object.strasse ?? "", summary?.object.strasse2 ?? ""].filter(Boolean).join(" ").trim();
    const city = [summary?.object.plz ?? "", summary?.object.ort ?? ""].filter(Boolean).join(" ").trim();
    return [street || "-", city].filter(Boolean).join(" | ");
  }, [summary?.object.ort, summary?.object.plz, summary?.object.strasse, summary?.object.strasse2]);

  const sumUnitStimmrecht = Number(unitsData?.meta.sumStimmrecht ?? unitsRows.reduce((sum, row) => sum + (Number(row.stimmrecht) || 0), 0));
  const sumUnitMea = Number(unitsData?.meta.sumMea ?? unitsRows.reduce((sum, row) => sum + (Number(row.mea) || 0), 0));
  const sumKopfstimmrecht = ownersRows.reduce((sum, row) => sum + (row.status === "aktiv" ? 1 : 0), 0);

  const unitCanCreate = unitForm.einheitsnummer.trim() && unitForm.einheitstyp && unitForm.mea !== "" && Number(unitForm.mea) >= 0 && unitForm.status;
  const unitCanEdit = editUnitForm && editUnitForm.einheitsnummer.trim() && editUnitForm.einheitstyp && editUnitForm.mea !== "" && Number(editUnitForm.mea) >= 0;
  const ownerCanCreate = ownerForm.eigentuemer_id.trim() && hasAnyOwnerName(ownerForm.name_1, ownerForm.name_2, ownerForm.name_3) && ownerForm.status;
  const ownerCanEdit = editOwnerForm && editOwnerForm.eigentuemer_id.trim() && hasAnyOwnerName(editOwnerForm.name_1, editOwnerForm.name_2, editOwnerForm.name_3);
  const assignmentCanCreate = assignmentForm.unitId && assignmentForm.ownerId && assignmentForm.status;
  const assignmentCanEdit = editAssignmentForm && editAssignmentForm.unitId && editAssignmentForm.ownerId && editAssignmentForm.status;
  const resolutionDateErrors = getDateFieldErrors(resolutionForm.startdatum || undefined, resolutionForm.endedatum || undefined);
  const editResolutionDateErrors = editResolutionForm ? getDateFieldErrors(editResolutionForm.startdatum || undefined, editResolutionForm.endedatum || undefined) : { startError: null, endError: null };
  const resolutionHasVorbeschlussTop = !!resolutionForm.vorbeschluss_top.trim();
  const resolutionHasVorbeschlussDate = !!resolutionForm.vorbeschluss_wev_datum;
  const resolutionVorbeschlussPairValid = resolutionHasVorbeschlussTop === resolutionHasVorbeschlussDate;
  const resolutionVorbeschlussValid = resolutionVorbeschlussPairValid;
  const editResolutionHasVorbeschlussTop = !!editResolutionForm?.vorbeschluss_top.trim();
  const editResolutionHasVorbeschlussDate = !!editResolutionForm?.vorbeschluss_wev_datum;
  const editResolutionVorbeschlussPairValid = editResolutionHasVorbeschlussTop === editResolutionHasVorbeschlussDate;
  const editResolutionVorbeschlussValid = editResolutionVorbeschlussPairValid;
const resolutionFaciliooRefFilled = !!resolutionForm.facilioo_vorgangsnummer.trim();
const editResolutionFaciliooRefFilled = !!editResolutionForm?.facilioo_vorgangsnummer.trim();
const resolutionFaciliooValid = !resolutionFaciliooRefFilled || (!!createFaciliooProcessTitle && !createFaciliooValidationError);
// Beim Bearbeiten nicht auf einen bereits geladenen Vorgangstitel warten.
// Die Pflichtprüfung erfolgt beim Klick auf Speichern in validateEditFaciliooProcess().
const editResolutionFaciliooValid = !editResolutionFaciliooRefFilled || !editFaciliooValidationError;
const resolutionCanCreate = resolutionForm.titel.trim() && resolutionForm.beschlusstext.trim() && resolutionForm.stimmberechtigte_einheiten.length > 0 && !resolutionDateErrors.startError && !resolutionDateErrors.endError && resolutionVorbeschlussValid && resolutionFaciliooValid;
const resolutionCanEdit = editResolutionForm && editResolutionForm.titel.trim() && editResolutionForm.beschlusstext.trim() && editResolutionForm.stimmberechtigte_einheiten.length > 0 && !editResolutionDateErrors.startError && !editResolutionDateErrors.endError && editResolutionVorbeschlussValid && editResolutionFaciliooValid;
  const resolutionNumberHint = "Wird systemseitig vergeben";
  const createResolutionNumber = nextResolutionNumberData?.beschlussnummer ?? "Wird beim Speichern vergeben";
  const objectDetailHasOpenForm =
    showCreateUnit ||
    showCreateOwner ||
    showCreateAssignment ||
    showCreateResolution ||
    !!editUnitForm ||
    !!editOwnerForm ||
    !!editAssignmentForm ||
    !!editResolutionForm;
  const objectDetailPageClassName =
    tab === "uebersicht" || tab === "einheiten" || tab === "eigentuemer" || tab === "zuordnungen" || tab === "umlaufbeschluesse"
      ? `resolutions-v2-page object-detail-units-fit${objectDetailHasOpenForm ? " object-detail-form-open" : ""}`
      : undefined;

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: "uebersicht", label: "Übersicht" },
    { key: "einheiten", label: "Einheiten" },
    { key: "eigentuemer", label: "Eigentümer" },
    { key: "zuordnungen", label: "Zuordnungen" },
    { key: "umlaufbeschluesse", label: "Umlaufbeschlüsse" }
  ];
  const visibleTabs = tabs;

  useEffect(() => {
    setResolutionPage(1);
  }, [resolutionSearch, resolutionStatusFilter, resolutionResultFilter, resolutionSortBy, resolutionSortDir]);

  useEffect(() => {
    if (resolutionForm.stimmrechtsart === "stimmrecht" && !objectCanUseStimmrecht) {
      setResolutionForm((prev) => ({
        ...prev,
        stimmrechtsart: resolveDefaultStimmrechtsart(objectStandardStimmrechtsart, objectHasStimmrechte)
      }));
    }
  }, [resolutionForm.stimmrechtsart, objectCanUseStimmrecht, objectStandardStimmrechtsart, objectHasStimmrechte]);

  useEffect(() => {
    if (!showCreateResolution) return;
    setResolutionForm((prev) => ({
      ...prev,
      stimmrechtsart: resolveDefaultStimmrechtsart(objectStandardStimmrechtsart, objectHasStimmrechte)
    }));
  }, [showCreateResolution, objectStandardStimmrechtsart, objectHasStimmrechte]);

  useEffect(() => {
    if (editResolutionForm?.stimmrechtsart === "stimmrecht" && !objectCanUseStimmrecht) {
      setEditResolutionForm((prev) =>
        prev
          ? { ...prev, stimmrechtsart: resolveDefaultStimmrechtsart(objectStandardStimmrechtsart, objectHasStimmrechte) }
          : prev
      );
    }
  }, [editResolutionForm?.stimmrechtsart, objectCanUseStimmrecht, objectStandardStimmrechtsart, objectHasStimmrechte]);

  useEffect(() => {
    setCreateFaciliooValidationError("");
    setCreateFaciliooProcessTitle("");
  }, [resolutionForm.facilioo_vorgangsnummer, id]);

  useEffect(() => {
    setEditFaciliooValidationError("");
    setEditFaciliooProcessTitle("");
  }, [editResolutionForm?.facilioo_vorgangsnummer, editResolutionForm?.objectId]);

  const validateCreateFaciliooProcess = async (): Promise<boolean> => {
    const processRef = resolutionForm.facilioo_vorgangsnummer.trim();
    if (!processRef) {
      setCreateFaciliooValidationError("");
      setCreateFaciliooProcessTitle("");
      return true;
    }
    if (createFaciliooProcessTitle && !createFaciliooValidationError) {
      return true;
    }
    if (!id) {
      setCreateFaciliooValidationError("Objekt konnte nicht bestimmt werden.");
      setCreateFaciliooProcessTitle("");
      return false;
    }
    setCreateFaciliooValidationPending(true);
    setCreateFaciliooValidationError("");
    try {
      const result = await api.validateFaciliooProcessForObject(id, processRef);
      const resolvedTitle = String(result.processTitle ?? "").trim();
      setCreateFaciliooProcessTitle(resolvedTitle || "(ohne Titel)");
      return true;
    } catch (error) {
      setCreateFaciliooProcessTitle("");
      setCreateFaciliooValidationError(normalizeValidationErrorMessage(error));
      return false;
    } finally {
      setCreateFaciliooValidationPending(false);
    }
  };

  const validateEditFaciliooProcess = async (): Promise<boolean> => {
    const processRef = editResolutionForm?.facilioo_vorgangsnummer.trim() ?? "";
    if (!processRef) {
      setEditFaciliooValidationError("");
      setEditFaciliooProcessTitle("");
      return true;
    }
    if (editFaciliooProcessTitle && !editFaciliooValidationError) {
      return true;
    }
    const targetObjectId = editResolutionForm?.objectId || id;
    if (!targetObjectId) {
      setEditFaciliooValidationError("Objekt konnte nicht bestimmt werden.");
      setEditFaciliooProcessTitle("");
      return false;
    }
    setEditFaciliooValidationPending(true);
    setEditFaciliooValidationError("");
    try {
      const result = await api.validateFaciliooProcessForObject(targetObjectId, processRef);
      const resolvedTitle = String(result.processTitle ?? "").trim();
      setEditFaciliooProcessTitle(resolvedTitle || "(ohne Titel)");
      return true;
    } catch (error) {
      setEditFaciliooProcessTitle("");
      setEditFaciliooValidationError(normalizeValidationErrorMessage(error));
      return false;
    } finally {
      setEditFaciliooValidationPending(false);
    }
  };

  const toggleResolutionSort = (field: ResolutionSortField) => {
    if (resolutionSortBy === field) {
      setResolutionSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setResolutionSortBy(field);
    setResolutionSortDir("asc");
  };

  const resolutionSortLabel = (field: ResolutionSortField, label: string) => (
    <button
      type="button"
      onClick={() => toggleResolutionSort(field)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6
      }}
      title={resolutionSortBy === field ? `Sortierung: ${resolutionSortDir === "asc" ? "aufsteigend" : "absteigend"}` : "Sortierung aktivieren"}
    >
      <span>{label}</span>
      <span aria-hidden="true">{resolutionSortBy === field ? (resolutionSortDir === "asc" ? "?" : "?") : "?"}</span>
    </button>
  );

  return (
    <div className={objectDetailPageClassName}>
      <section className="page-header panel resolutions-v2-page-header object-detail-v2-header">
        <h2 className="resolutions-v2-title object-detail-v2-title" style={{ margin: 0 }}>
          <span className="resolutions-v2-title-icon object-detail-v2-head-icon" aria-hidden="true"><ObjectDetailIcon name="building" /></span>
          <span className="resolutions-v2-title-copy">
            <span className="resolutions-v2-title-main">{summary?.object.objektnummer ?? "-"} - {summary?.object.objektname ?? "Objekt"}</span>
            <span className="resolutions-v2-title-sub">
              {summary?.object.strasse ?? ""}{summary?.object.strasse2 ? ` ${summary.object.strasse2}` : ""} | {summary?.object.plz ?? ""} {summary?.object.ort ?? ""}
            </span>
          </span>
        </h2>
        {canManageMasterDataActions ? (
          <button className="resolutions-v2-create-btn object-detail-v2-edit-btn" onClick={() => navigate("/objects", { state: { editObjectId: id } })}>
            <span className="resolutions-v2-create-btn-icon" aria-hidden="true"><ObjectDetailIcon name="edit" /></span>
            <span>Objekt bearbeiten</span>
          </button>
        ) : null}
      </section>

      <section className="panel object-tabbar object-tabbar-v2">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            className={`object-tabbtn object-tabbtn-v2 ${tab === t.key ? "active" : ""} ${t.key === "umlaufbeschluesse" ? "object-tabbtn--resolutions" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </section>

      {tab === "uebersicht" ? (
        <>
          <section className="panel object-overview-grid object-overview-grid-v2">
            <div className="kpi object-overview-kpi-v2">
              <span className="object-overview-kpi-icon" aria-hidden="true"><ObjectDetailIcon name="resolution" /></span>
              <div className="object-overview-kpi-copy">
                <span>Umlaufbeschlüsse</span>
                <strong>{resolutionOverviewStats.total}</strong>
                <small>Gesamt</small>
                <div className="kpi-unit-type-badges">
                  <span className="badge object-v2-pill-running">Laufend: {resolutionOverviewStats.aktiv}</span>
                  <span className="badge object-v2-pill-ended">Beendet: {resolutionOverviewStats.abgeschlossen}</span>
                  <span className="badge object-v2-pill-draft">Entwürfe: {resolutionOverviewStats.entwurf}</span>
                </div>
              </div>
            </div>
            <div className="kpi object-overview-kpi-v2">
              <span className="object-overview-kpi-icon" aria-hidden="true"><ObjectDetailIcon name="home" /></span>
              <div className="object-overview-kpi-copy">
                <span>Einheiten</span>
                <strong>{unitsOverviewTotal}</strong>
                <small>Gesamt</small>
                {unitTypeOverviewBadges.length ? (
                  <div className="kpi-unit-type-badges">
                    {unitTypeOverviewBadges.map((item) => (
                      <span key={item.label} className="badge object-v2-pill-gray">
                        {item.label}: {item.count}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="kpi object-overview-kpi-v2">
              <span className="object-overview-kpi-icon" aria-hidden="true"><ObjectDetailIcon name="owners" /></span>
              <div className="object-overview-kpi-copy">
                <span>Eigentümer</span>
                <strong>{ownerOverviewStats.total}</strong>
                <small>Gesamt</small>
                <div className="kpi-unit-type-badges">
                  <span className="badge object-v2-pill-green">Digital erreichbar: {ownerOverviewStats.digital}</span>
                  <span className="badge object-v2-pill-ended">Nicht digital erreichbar: {ownerOverviewStats.analog}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="panel object-overview-detail-grid-v2">
            <article className="object-overview-detail-card-v2">
              <h3 className="object-overview-card-title-v2">
                <span className="object-overview-card-title-icon-v2" aria-hidden="true"><ObjectDetailIcon name="resolution" /></span>
                <span>Letzte 5 Umlaufbeschlüsse</span>
              </h3>
              {latestResolutionOverviewRows.length ? (
                <div className="object-overview-resolution-list-v2">
                  {latestResolutionOverviewRows.map((resolution) => (
                    <div key={resolution.id} className="object-overview-resolution-row-v2">
                      <span className={`object-overview-resolution-icon-v2 object-overview-resolution-icon-v2--${resolution.status}`} aria-hidden="true">
                        <ObjectDetailIcon name="resolution" />
                      </span>
                      <div className="object-overview-resolution-main-v2">
                        <div className="object-overview-resolution-title-line-v2">
                          <Link className="object-overview-resolution-title-v2" to={`/resolutions/${resolution.id}`} title={resolution.titel}>
                            {resolution.titel || "Ohne Titel"}
                          </Link>
                          <span className={`badge ${resolution.status}`}>{formatResolutionStatus(resolution.status)}</span>
                        </div>
                        <span>Beschluss-Nr. {resolution.beschlussnummer}</span>
                      </div>
                      <div className="object-overview-resolution-meta-v2">
                        <time>{formatDateDE(resolution.endedatum ?? resolution.startdatum ?? resolution.createdAt)}</time>
                        {(() => {
                          const outcome = getResolutionOverviewOutcome(resolution);
                          return (
                            <span className={`object-overview-resolution-result-v2 object-overview-resolution-result-v2--${outcome.tone ?? "neutral"}`}>
                              Ergebnis: {outcome.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="object-overview-resolution-empty-v2">Für dieses Objekt liegen noch keine Umlaufbeschlüsse vor.</div>
              )}
            </article>

            <article className="object-overview-detail-card-v2">
              <h3 className="object-overview-card-title-v2">
                <span className="object-overview-card-title-icon-v2" aria-hidden="true"><ObjectDetailIcon name="building" /></span>
                <span>Objektinformationen</span>
              </h3>
              <div className="object-overview-info-list-v2">
                <div>
                  <span className="object-overview-info-icon" aria-hidden="true">#</span>
                  <span>Objektnummer</span>
                  <strong>{summary?.object.objektnummer ?? "-"}</strong>
                </div>
                <div>
                  <span className="object-overview-info-icon" aria-hidden="true"><ObjectDetailIcon name="pin" /></span>
                  <span>Adresse</span>
                  <strong>{objectAddressLabel}</strong>
                </div>
                <div>
                  <span className="object-overview-info-icon" aria-hidden="true"><ObjectDetailIcon name="building" /></span>
                  <span>Objekttyp</span>
                  <strong>Wohnungseigentümergemeinschaft</strong>
                </div>
                <div>
                  <span className="object-overview-info-icon" aria-hidden="true"><ObjectDetailIcon name="calendar" /></span>
                  <span>Erstellt am</span>
                  <strong>{formatDateTimeDE(summary?.object.createdAt)} Uhr</strong>
                </div>
                <div>
                  <span className="object-overview-info-icon" aria-hidden="true"><ObjectDetailIcon name="refresh" /></span>
                  <span>Zuletzt aktualisiert</span>
                  <strong>{formatDateTimeDE(summary?.object.updatedAt)} Uhr</strong>
                </div>
              </div>
            </article>

            <article className="object-overview-detail-card-v2">
              <h3 className="object-overview-card-title-v2">
                <span className="object-overview-card-title-icon-v2" aria-hidden="true"><ObjectDetailIcon name="reachability" /></span>
                <span>Digitale Erreichbarkeit</span>
              </h3>
              <div className="object-reachability-v2">
                <div className="object-reachability-ring-v2" style={reachabilityRingStyle}>
                  <div className="object-reachability-ring-inner-v2">
                    <strong>{Math.round(reachabilityRingPct)}%</strong>
                    <span>digital erreichbar</span>
                  </div>
                </div>
                <div className="object-reachability-legend-v2">
                  <div className="reachability-line reachability-line-digital">
                    <span className="dot dot-digital" /> <strong>Digital erreichbar</strong>
                    <small>{ownerOverviewStats.digital} Eigentümer</small>
                    <b>{ownerOverviewDigitalPctLabel} %</b>
                  </div>
                  <div className="reachability-line reachability-line-analog">
                    <span className="dot dot-analog" /> <strong>Nicht digital erreichbar</strong>
                    <small>{ownerOverviewStats.analog} Eigentümer</small>
                    <b>{ownerOverviewAnalogPctLabel} %</b>
                  </div>
                  <div className="reachability-total">
                    <span>Gesamt</span>
                    <strong>{ownerOverviewStats.total} Eigentümer</strong>
                  </div>
                </div>
              </div>
            </article>
          </section>

          <section className="object-overview-footer-row-v2">
            <div className={`panel object-overview-footer-v2 v2-info-banner ${objectIsActive ? "object-overview-footer-v2--active" : "object-overview-footer-v2--inactive"}`}>
              <span className="imports-sync-info-icon object-overview-footer-icon" aria-hidden="true">i</span>
              <div className="imports-sync-info-copy">
                <strong>{objectIsActive ? "Dieses Objekt ist aktiv" : "Dieses Objekt ist inaktiv"}</strong>
                <span>
                  {objectIsActive
                    ? "Sie können Einheiten, Eigentümer und Umlaufbeschlüsse verwalten sowie neue Abstimmungen erstellen."
                    : "Dieses Objekt ist deaktiviert. Neue Umlaufbeschlüsse können nicht angelegt werden."}
                </span>
              </div>
            </div>
            {canCreateResolutions && objectIsActive ? (
              <button
                className="resolutions-v2-create-btn object-overview-footer-create-btn"
                onClick={() => {
                  setTab("umlaufbeschluesse");
                  setShowCreateResolution(true);
                }}
              >
                <span className="resolutions-v2-create-btn-icon" aria-hidden="true"><ObjectDetailIcon name="plus" /></span>
                <span>Umlaufbeschluss erstellen</span>
              </button>
            ) : null}
          </section>
        </>
      ) : null}

      {tab === "einheiten" ? (
        <>
          <section className="page-header panel resolutions-v2-page-header">
            <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
              <span className="resolutions-v2-title-icon" aria-hidden="true"><ObjectDetailIcon name="home" /></span>
              <span className="resolutions-v2-title-copy">
                <span className="resolutions-v2-title-main">Einheiten</span>
                <span className="resolutions-v2-title-sub">Übersicht aller Einheiten und Nutzungsdaten</span>
              </span>
            </h2>
            {canManageMasterDataActions ? (
              <button className="resolutions-v2-create-btn" onClick={() => setShowCreateUnit((v) => !v)} disabled={objectLocked}>
                {!showCreateUnit ? <span className="resolutions-v2-create-btn-icon" aria-hidden="true"><ObjectDetailIcon name="plus" /></span> : null}
                <span>{showCreateUnit ? "Schließen" : "Neue Einheit"}</span>
              </button>
            ) : null}
          </section>

          {canManageMasterDataActions && showCreateUnit ? (
            <section className="panel create-form resolution-create-form">
              <div className="form-grid">
                <div className="form-field"><label>Einheitsnummer*</label><input value={unitForm.einheitsnummer} onChange={(e) => setUnitForm({ ...unitForm, einheitsnummer: e.target.value })} /></div>
                <div className="form-field"><label>Lage</label><input value={unitForm.bezeichnung} onChange={(e) => setUnitForm({ ...unitForm, bezeichnung: e.target.value })} /></div>
                <div className="form-field"><label>Einheitstyp*</label><select value={unitForm.einheitstyp} onChange={(e) => setUnitForm({ ...unitForm, einheitstyp: e.target.value as UnitTypeOption })}><option value="">Einheitstyp wählen*</option>{FACILIOO_UNIT_TYPE_VALUES.map((value) => <option key={value} value={value}>{formatUnitTypeLabel(value)}</option>)}</select></div>
                <div className="form-field"><label>Stimmrecht</label><input type="number" min="0" step="0.01" value={unitForm.stimmrecht} onChange={(e) => setUnitForm({ ...unitForm, stimmrecht: e.target.value })} /></div>
                <div className="form-field"><label>MEA*</label><input type="number" min="0" step="0.01" value={unitForm.mea} onChange={(e) => setUnitForm({ ...unitForm, mea: e.target.value })} /></div>
                <div className="form-field"><label>Status*</label><select value={unitForm.status} onChange={(e) => setUnitForm({ ...unitForm, status: e.target.value as StatusOption })}><option value="">Status wählen*</option><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
              </div>
              <div className="form-actions"><button disabled={!unitCanCreate || createUnitMutation.isPending || objectLocked} onClick={() => createUnitMutation.mutate()}>{createUnitMutation.isPending ? "Speichere..." : "Einheit speichern"}</button></div>
            </section>
          ) : null}

          {!showCreateUnit && canManageMasterDataActions && editUnitForm ? (
            <section className="panel create-form resolution-create-form">
              <div className="form-grid">
                <div className="form-field"><label>Einheitsnummer*</label><input value={editUnitForm.einheitsnummer} onChange={(e) => setEditUnitForm({ ...editUnitForm, einheitsnummer: e.target.value })} /></div>
                <div className="form-field"><label>Lage</label><input value={editUnitForm.bezeichnung} onChange={(e) => setEditUnitForm({ ...editUnitForm, bezeichnung: e.target.value })} /></div>
                <div className="form-field"><label>Einheitstyp*</label><select value={editUnitForm.einheitstyp} onChange={(e) => setEditUnitForm({ ...editUnitForm, einheitstyp: e.target.value })}>{FACILIOO_UNIT_TYPE_VALUES.map((value) => <option key={value} value={value}>{formatUnitTypeLabel(value)}</option>)}</select></div>
                <div className="form-field"><label>Stimmrecht</label><input type="number" min="0" step="0.01" value={editUnitForm.stimmrecht} onChange={(e) => setEditUnitForm({ ...editUnitForm, stimmrecht: e.target.value })} /></div>
                <div className="form-field"><label>MEA*</label><input type="number" min="0" step="0.01" value={editUnitForm.mea} onChange={(e) => setEditUnitForm({ ...editUnitForm, mea: e.target.value })} /></div>
                <div className="form-field"><label>Status*</label><select value={editUnitForm.status} onChange={(e) => setEditUnitForm({ ...editUnitForm, status: e.target.value as "aktiv" | "inaktiv" })}><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
              </div>
              <div className="form-actions"><button disabled={!unitCanEdit || updateUnitMutation.isPending || objectLocked} onClick={() => updateUnitMutation.mutate()}>{updateUnitMutation.isPending ? "Speichere..." : "Änderungen speichern"}</button><button onClick={() => setEditUnitForm(null)}>Abbrechen</button></div>
            </section>
          ) : null}

          {!showCreateUnit ? (
            <section className="panel resolutions-v2-list-panel object-detail-units-list-panel">
              <div className="resolutions-v2-filterbar">
                <div className="resolutions-v2-filter-fields">
                  <label className="resolutions-v2-filter-input">
                    <span className="resolutions-v2-filter-icon" aria-hidden="true">{unitListIcons.search}</span>
                    <input placeholder="Suche..." value={unitSearch} onChange={(e) => setUnitSearch(e.target.value)} />
                  </label>
                  <select value={unitAufgangFilter} onChange={(e) => setUnitAufgangFilter(e.target.value)}>
                    <option value="">Alle Aufgänge</option>
                    {unitAufgangOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                  <select value={unitTypeFilter} onChange={(e) => setUnitTypeFilter(e.target.value as UnitTypeOption)}>
                    <option value="">Alle Einheitstypen</option>
                    {FACILIOO_UNIT_TYPE_VALUES.map((value) => <option key={value} value={value}>{formatUnitTypeLabel(value)}</option>)}
                  </select>
                  <select value={unitStatus} onChange={(e) => setUnitStatus(e.target.value as StatusOption)}>
                    <option value="">Alle Status</option>
                    <option value="aktiv">Aktiv</option>
                    <option value="inaktiv">Inaktiv</option>
                  </select>
                </div>
                <button type="button" className="resolutions-v2-reset-btn" onClick={() => { setUnitSearch(""); setUnitAufgangFilter(""); setUnitTypeFilter(""); setUnitStatus("aktiv"); setUnitSortBy("einheitsnummer"); setUnitSortDir("asc"); setUnitPage(1); }}>
                  <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">{unitListIcons.reset}</span>
                  <span>Filter zurücksetzen</span>
                </button>
              </div>

              <div className="resolutions-v2-row-count">Zeilen: {unitsRows.length} / Gesamt: {unitTotalRows}</div>

              <div className="resolutions-v2-table-wrap">
                <table className="resolutions-v2-table units-v2-table">
                  <thead>
                    <tr>
                      <th>{renderUnitSortHeader("einheitsnummer", "Einheit-Nr", <ObjectDetailIcon name="home" />)}</th>
                      <th>{plainUnitHeader("Aufgang", unitListIcons.entrance)}</th>
                      <th>{renderUnitSortHeader("bezeichnung", "Lage", unitListIcons.location)}</th>
                      <th>{plainUnitHeader("Einheitstyp", unitListIcons.type)}</th>
                      <th>{plainUnitHeader("Stimmrecht", unitListIcons.votes)}</th>
                      <th>{plainUnitHeader("MEA", unitListIcons.mea)}</th>
                      <th>{plainUnitHeader("Status", unitListIcons.status)}</th>
                      <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">{plainUnitHeader("", unitListIcons.actions)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitsRows.map((r) => (
                      <tr key={r.id}>
                        <td>{formatEinheitsnummer(r.einheitsnummer)}</td>
                        <td>{r.aufgangName || r.aufgangRef || "-"}</td>
                        <td className="resolutions-v2-object-cell">{r.bezeichnung || "-"}</td>
                        <td>{formatUnitTypeLabel(r.einheitstyp)}</td>
                        <td>{formatNumberDE(r.stimmrecht)}</td>
                        <td>{formatNumberDE(r.mea)}</td>
                        <td><span className={`badge ${r.status}`}>{r.status}</span></td>
                        <td>
                          {!canManageMasterDataActions ? (
                            <span style={{ color: "var(--text-soft)" }}>-</span>
                          ) : (
                            <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="resolutions-v2-action-trigger" aria-label={`Aktionen für Einheit ${r.einheitsnummer}`} aria-expanded={unitOpenActionMenuId === r.id} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (unitOpenActionMenuId === r.id) { setUnitOpenActionMenuId(null); return; } openDetailActionMenu(r.id, e.currentTarget, setUnitOpenActionMenuId, 96); }}>&#8942;</button>
                              {unitOpenActionMenuId === r.id && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                                <div className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed" style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }} onMouseDown={(e) => e.stopPropagation()} onMouseEnter={() => setIsActionMenuHovered(true)} onMouseLeave={() => setIsActionMenuHovered(false)}>
                                  <button type="button" disabled={objectLocked} onClick={() => { setEditUnitForm({ id: r.id, einheitsnummer: r.einheitsnummer, bezeichnung: r.bezeichnung, einheitstyp: String(r.einheitstyp ?? "").trim().toLowerCase(), stimmrecht: String(r.stimmrecht), mea: r.mea !== undefined ? String(r.mea) : "", status: r.status }); setUnitOpenActionMenuId(null); }}>Bearbeiten</button>
                                  <button type="button" disabled={objectLocked} onClick={() => { setDeleteConfirm({ id: `unit:${r.id}`, label: r.einheitsnummer, code: String(Math.floor(1000 + Math.random() * 9000)), input: ["", "", "", ""], error: "" }); setUnitOpenActionMenuId(null); }}>Löschen</button>
                                </div>,
                                document.body
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="resolutions-v2-pagination">
                <div className="resolutions-v2-page-size">
                  <select value={String(unitPageSize)} onChange={(e) => { setUnitPageSize(Number(e.target.value) || 10); setUnitPage(1); }}>
                    <option value="10">10 pro Seite</option>
                    <option value="20">20 pro Seite</option>
                    <option value="50">50 pro Seite</option>
                  </select>
                </div>
                <div className="resolutions-v2-page-center">
                  <button type="button" className="resolutions-v2-page-arrow" onClick={() => setUnitPage((prev) => Math.max(1, prev - 1))} disabled={unitPage <= 1}>‹</button>
                  {unitPageItems.map((pageItem) => (
                    <button key={pageItem} type="button" className={`resolutions-v2-page-chip ${pageItem === unitPage ? "active" : ""}`} onClick={() => setUnitPage(pageItem)}>{pageItem}</button>
                  ))}
                  <button type="button" className="resolutions-v2-page-arrow" onClick={() => setUnitPage((prev) => Math.min(unitTotalPages, prev + 1))} disabled={unitPage >= unitTotalPages}>›</button>
                  <span className="resolutions-v2-page-info">Seite {unitPage} von {unitTotalPages}</span>
                </div>
                <div className="resolutions-v2-page-next-wrap">
                  <button type="button" className="resolutions-v2-next-btn" onClick={() => setUnitPage((prev) => Math.min(unitTotalPages, prev + 1))} disabled={unitPage >= unitTotalPages}>
                    <span>Weiter</span>
                    <span aria-hidden="true">›</span>
                  </button>
                </div>
              </div>
            </section>
          ) : null}
          {!showCreateUnit ? <section className="panel"><strong>Summe</strong> | Stimmrechte: {sumUnitStimmrecht.toFixed(2)} | MEA: {sumUnitMea.toFixed(2)}</section> : null}
        </>
      ) : null}

      {tab === "eigentuemer" ? (
        <>
          <section className="page-header panel resolutions-v2-page-header">
            <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
              <span className="resolutions-v2-title-icon" aria-hidden="true"><ObjectDetailIcon name="owners" /></span>
              <span className="resolutions-v2-title-copy">
                <span className="resolutions-v2-title-main">Eigentümer</span>
                <span className="resolutions-v2-title-sub">Übersicht aller Eigentümer dieses Objekts</span>
              </span>
            </h2>
            {canManageMasterDataActions ? (
              <button className="resolutions-v2-create-btn" onClick={() => setShowCreateOwner((v) => !v)} disabled={objectLocked}>
                {!showCreateOwner ? <span className="resolutions-v2-create-btn-icon" aria-hidden="true"><ObjectDetailIcon name="plus" /></span> : null}
                <span>{showCreateOwner ? "Schließen" : "Neuer Eigentümer"}</span>
              </button>
            ) : null}
          </section>

          {canManageMasterDataActions && showCreateOwner ? (
            <section className="panel create-form resolution-create-form">
              <div className="form-grid">
                <div className="form-field"><label>Eigentümer ID*</label><input value={ownerForm.eigentuemer_id} onChange={(e) => setOwnerForm({ ...ownerForm, eigentuemer_id: e.target.value })} /></div>
                <div className="form-field"><label>Anrede</label><select value={ownerForm.anrede} onChange={(e) => setOwnerForm({ ...ownerForm, anrede: e.target.value })}><option value="">Anrede wählen</option>{anredeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                <div className="form-field"><label>Titel</label><select value={ownerForm.titel} onChange={(e) => setOwnerForm({ ...ownerForm, titel: e.target.value })}><option value="">Titel wählen</option>{titelOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                <div className="form-field"><label>Vorname</label><input value={ownerForm.vorname} onChange={(e) => setOwnerForm({ ...ownerForm, vorname: e.target.value })} /></div><div className="form-field"><label>Name 1*</label><input value={ownerForm.name_1} onChange={(e) => setOwnerForm({ ...ownerForm, name_1: e.target.value })} /></div>
                <div className="form-field"><label>Name 2</label><input value={ownerForm.name_2} onChange={(e) => setOwnerForm({ ...ownerForm, name_2: e.target.value })} /></div>
                <div className="form-field"><label>Name 3</label><input value={ownerForm.name_3} onChange={(e) => setOwnerForm({ ...ownerForm, name_3: e.target.value })} /></div>
                <div className="form-field"><label>Straße / Postfach</label><input value={ownerForm.strasse} onChange={(e) => setOwnerForm({ ...ownerForm, strasse: e.target.value })} /></div>
                <div className="form-field"><label>PLZ</label><input value={ownerForm.plz} onChange={(e) => setOwnerForm({ ...ownerForm, plz: e.target.value })} /></div>
                <div className="form-field"><label>Ort</label><input value={ownerForm.ort} onChange={(e) => setOwnerForm({ ...ownerForm, ort: e.target.value })} /></div>
                <div className="form-field"><label>Länder-Code</label><input value={ownerForm.land_code} onChange={(e) => setOwnerForm({ ...ownerForm, land_code: e.target.value.toUpperCase() })} /></div>
                <div className="form-field"><label>Land</label><input value={ownerForm.land} onChange={(e) => setOwnerForm({ ...ownerForm, land: e.target.value })} /></div>
                <div className="form-field"><label>E-Mail</label><input value={ownerForm.email} onChange={(e) => setOwnerForm({ ...ownerForm, email: e.target.value })} /></div>
                <div className="form-field"><label>Telefon</label><input value={ownerForm.telefon} onChange={(e) => setOwnerForm({ ...ownerForm, telefon: e.target.value })} /></div>
                <div className="form-field"><label>Status*</label><select value={ownerForm.status} onChange={(e) => setOwnerForm({ ...ownerForm, status: e.target.value as StatusOption })}><option value="">Status wählen*</option><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
              </div>
              <div className="form-actions"><button disabled={!ownerCanCreate || createOwnerMutation.isPending || objectLocked} onClick={() => createOwnerMutation.mutate()}>{createOwnerMutation.isPending ? "Speichere..." : "Eigentümer speichern"}</button></div>
            </section>
          ) : null}

          {!showCreateOwner && canManageMasterDataActions && editOwnerForm ? (
            <section className="panel create-form resolution-create-form">
              <div className="form-grid">
                <div className="form-field"><label>Eigentümer ID*</label><input value={editOwnerForm.eigentuemer_id} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, eigentuemer_id: e.target.value })} /></div>
                <div className="form-field"><label>Anrede</label><select value={editOwnerForm.anrede} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, anrede: e.target.value })}><option value="">Anrede wählen</option>{anredeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                <div className="form-field"><label>Titel</label><select value={editOwnerForm.titel} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, titel: e.target.value })}><option value="">Titel wählen</option>{titelOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                <div className="form-field"><label>Vorname</label><input value={editOwnerForm.vorname} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, vorname: e.target.value })} /></div><div className="form-field"><label>Name 1*</label><input value={editOwnerForm.name_1} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, name_1: e.target.value })} /></div>
                <div className="form-field"><label>Name 2</label><input value={editOwnerForm.name_2} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, name_2: e.target.value })} /></div>
                <div className="form-field"><label>Name 3</label><input value={editOwnerForm.name_3} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, name_3: e.target.value })} /></div>
                <div className="form-field"><label>Straße / Postfach</label><input value={editOwnerForm.strasse} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, strasse: e.target.value })} /></div>
                <div className="form-field"><label>PLZ</label><input value={editOwnerForm.plz} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, plz: e.target.value })} /></div>
                <div className="form-field"><label>Ort</label><input value={editOwnerForm.ort} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, ort: e.target.value })} /></div>
                <div className="form-field"><label>Länder-Code</label><input value={editOwnerForm.land_code} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, land_code: e.target.value.toUpperCase() })} /></div>
                <div className="form-field"><label>Land</label><input value={editOwnerForm.land} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, land: e.target.value })} /></div>
                <div className="form-field"><label>E-Mail</label><input value={editOwnerForm.email} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, email: e.target.value })} /></div>
                <div className="form-field"><label>Telefon</label><input value={editOwnerForm.telefon} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, telefon: e.target.value })} /></div>
                <div className="form-field"><label>Status*</label><select value={editOwnerForm.status} onChange={(e) => setEditOwnerForm({ ...editOwnerForm, status: e.target.value as "aktiv" | "inaktiv" })}><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
              </div>
              <div className="form-actions"><button disabled={!ownerCanEdit || updateOwnerMutation.isPending || objectLocked} onClick={() => updateOwnerMutation.mutate()}>{updateOwnerMutation.isPending ? "Speichere..." : "Änderungen speichern"}</button><button onClick={() => setEditOwnerForm(null)}>Abbrechen</button></div>
            </section>
          ) : null}

          {!showCreateOwner ? (
            <section className="panel resolutions-v2-list-panel object-detail-units-list-panel">
              <div className="resolutions-v2-filterbar">
                <div className="resolutions-v2-filter-fields">
                  <label className="resolutions-v2-filter-input">
                    <span className="resolutions-v2-filter-icon" aria-hidden="true">{unitListIcons.search}</span>
                    <input placeholder="Suche..." value={ownerSearchFilter} onChange={(e) => setOwnerSearchFilter(e.target.value)} />
                  </label>
                  <select value={ownerReachabilityFilter} onChange={(e) => setOwnerReachabilityFilter(e.target.value as "" | "digital" | "analog")}>
                    <option value="">Alle Erreichbarkeit</option>
                    <option value="digital">Digital</option>
                    <option value="analog">Analog</option>
                  </select>
                  <select value={ownerStatusFilter} onChange={(e) => setOwnerStatusFilter(e.target.value as OwnerStatusFilterOption)}>
                    <option value="">Alle Status</option>
                    <option value="aktiv">Aktiv</option>
                    <option value="inaktiv">Inaktiv</option>
                    <option value="keine_zuordnung">Keine Zuordnung</option>
                  </select>
                </div>
                <button type="button" className="resolutions-v2-reset-btn" onClick={() => { setOwnerSearchFilter(""); setOwnerReachabilityFilter(""); setOwnerStatusFilter("aktiv"); setOwnerPage(1); }}>
                  <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">{unitListIcons.reset}</span>
                  <span>Filter zurücksetzen</span>
                </button>
              </div>
              <div className="resolutions-v2-row-count">Zeilen: {ownersRows.length} / Gesamt: {ownerTotalRows}</div>
              <div className="resolutions-v2-table-wrap">
                <table className="resolutions-v2-table owners-v2-table">
                  <thead>
                    <tr>
                      <th>
                        <span className="resolutions-v2-th-plain">
                          <span className="resolutions-v2-th-icon" aria-hidden="true">
                            <svg viewBox="0 0 20 20" fill="none">
                              <circle cx="10" cy="7.4" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                              <path d="M5.2 15.9a4.8 4.8 0 0 1 9.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                            </svg>
                          </span>
                          <span className="resolutions-v2-th-label">Eigentümer-ID</span>
                        </span>
                      </th>
                      <th>{plainUnitHeader("Eigentümername", <ObjectDetailIcon name="owners" />)}</th>
                      <th>{plainUnitHeader("E-Mail", <ObjectDetailIcon name="edit" />)}</th>
                      <th>{plainUnitHeader("Erreichbarkeit", <ObjectDetailIcon name="reachability" />)}</th>
                      <th>{plainUnitHeader("Status", unitListIcons.status)}</th>
                      <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">{plainUnitHeader("", unitListIcons.actions)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownersRows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.eigentuemer_id}</td>
                        <td>{[r.anrede, r.titel, r.vorname, r.name_1, r.name_2, r.name_3].map((part) => String(part ?? "").trim()).filter(Boolean).join(" ") || r.vollname || "-"}</td>
                        <td>{r.email || "-"}</td>
                        <td><span className={`badge ${r.ist_analog ? "erreichbar-analog" : "erreichbar-digital"}`}>{r.ist_analog ? "analog" : "digital"}</span></td>
                        <td><span className={`badge ${r.status} quick-tooltip`} data-tooltip={buildOwnerValidityTooltip(r)} tabIndex={0}>{formatOwnerStatusLabel(r.status)}</span></td>
                        <td>
                          {!canManageMasterDataActions ? <span style={{ color: "var(--text-soft)" }}>-</span> : (
                            <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="resolutions-v2-action-trigger" aria-label={`Aktionen für Eigentümer ${r.eigentuemer_id}`} aria-expanded={ownerOpenActionMenuId === r.id} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (ownerOpenActionMenuId === r.id) { setOwnerOpenActionMenuId(null); return; } openDetailActionMenu(r.id, e.currentTarget, setOwnerOpenActionMenuId, 96); }}>&#8942;</button>
                              {ownerOpenActionMenuId === r.id && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                                <div className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed" style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }} onMouseDown={(e) => e.stopPropagation()} onMouseEnter={() => setIsActionMenuHovered(true)} onMouseLeave={() => setIsActionMenuHovered(false)}>
                                  <button type="button" disabled={objectLocked} onClick={() => { setEditOwnerForm({ id: r.id, eigentuemer_id: r.eigentuemer_id, anrede: r.anrede ?? "", titel: r.titel ?? "", vorname: r.vorname ?? "", name_1: r.name_1 ?? "", name_2: r.name_2 ?? "", name_3: r.name_3 ?? "", strasse: r.strasse ?? "", plz: r.plz ?? "", ort: r.ort ?? "", land_code: r.land_code ?? "", land: r.land ?? "", email: r.email ?? "", telefon: r.telefon ?? "", status: r.status === "inaktiv" ? "inaktiv" : "aktiv" }); setOwnerOpenActionMenuId(null); }}>Bearbeiten</button>
                                  <button type="button" disabled={objectLocked} onClick={() => { setDeleteConfirm({ id: `owner:${r.id}`, label: r.eigentuemer_id, code: String(Math.floor(1000 + Math.random() * 9000)), input: ["", "", "", ""], error: "" }); setOwnerOpenActionMenuId(null); }}>Löschen</button>
                                </div>,
                                document.body
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="resolutions-v2-pagination">
                <div className="resolutions-v2-page-size">
                  <select value={String(ownerPageSize)} onChange={(e) => { setOwnerPageSize(Number(e.target.value) || 10); setOwnerPage(1); }}>
                    <option value="10">10 pro Seite</option>
                    <option value="20">20 pro Seite</option>
                    <option value="50">50 pro Seite</option>
                  </select>
                </div>
                <div className="resolutions-v2-page-center">
                  <button type="button" className="resolutions-v2-page-arrow" onClick={() => setOwnerPage((prev) => Math.max(1, prev - 1))} disabled={ownerPage <= 1}>‹</button>
                  {ownerPageItems.map((pageItem) => <button key={pageItem} type="button" className={`resolutions-v2-page-chip ${pageItem === ownerPage ? "active" : ""}`} onClick={() => setOwnerPage(pageItem)}>{pageItem}</button>)}
                  <button type="button" className="resolutions-v2-page-arrow" onClick={() => setOwnerPage((prev) => Math.min(ownerTotalPages, prev + 1))} disabled={ownerPage >= ownerTotalPages}>›</button>
                  <span className="resolutions-v2-page-info">Seite {ownerPage} von {ownerTotalPages}</span>
                </div>
                <div className="resolutions-v2-page-next-wrap">
                  <button type="button" className="resolutions-v2-next-btn" onClick={() => setOwnerPage((prev) => Math.min(ownerTotalPages, prev + 1))} disabled={ownerPage >= ownerTotalPages}><span>Weiter</span><span aria-hidden="true">›</span></button>
                </div>
              </div>
            </section>
          ) : null}
          {!showCreateOwner ? <section className="panel"><strong>Summe</strong> | Kopfstimmrecht (nur aktive Eigentümer): {sumKopfstimmrecht}</section> : null}
        </>
      ) : null}

      {tab === "zuordnungen" ? (
        <>
          <section className="page-header panel resolutions-v2-page-header">
            <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
              <span className="resolutions-v2-title-icon" aria-hidden="true"><ObjectDetailIcon name="assignments" /></span>
              <span className="resolutions-v2-title-copy">
                <span className="resolutions-v2-title-main">Zuordnungen</span>
                <span className="resolutions-v2-title-sub">Übersicht aller Zuordnungen dieses Objekts</span>
              </span>
            </h2>
            {canManageMasterDataActions ? (
              <button className="resolutions-v2-create-btn" onClick={() => setShowCreateAssignment((v) => !v)} disabled={objectLocked}>
                {!showCreateAssignment ? <span className="resolutions-v2-create-btn-icon" aria-hidden="true"><ObjectDetailIcon name="plus" /></span> : null}
                <span>{showCreateAssignment ? "Schließen" : "Neue Zuordnung"}</span>
              </button>
            ) : null}
          </section>

          {canManageMasterDataActions && showCreateAssignment ? (
            <section className="panel create-form resolution-create-form">
              <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1.6fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(140px, 0.7fr)", gap: 10, alignItems: "start" }}>
                <div className="form-field">
                  <label>Eigentümer*</label>
                  <input
                    placeholder="Eigentümer suchen (ID, Name, E-Mail)"
                    value={assignmentOwnerSearch}
                    onChange={(e) => setAssignmentOwnerSearch(e.target.value)}
                  />
                  <label style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      checked={assignmentOnlyUnassigned}
                      onChange={(e) => setAssignmentOnlyUnassigned(e.target.checked)}
                    />
                    Nur ohne Zuordnung
                  </label>
                </div>
                <div className="form-field">
                  <label>Objekt*</label>
                  <input value={`${summary?.object.objektnummer ?? "-"} - ${summary?.object.objektname ?? "-"}`} disabled />
                </div>
                <div className="form-field">
                  <label>Einheit*</label>
                  <select value={assignmentForm.unitId} onChange={(e) => setAssignmentForm({ ...assignmentForm, unitId: e.target.value })}>
                    <option value="">Einheit wählen*</option>
                    {(unitsForSelectionData?.data ?? []).map((u) => (
                      <option key={u.id} value={u.id}>{`${u.einheitsnummer} - ${u.bezeichnung || "-"}`}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Status*</label>
                  <select value={assignmentForm.status} onChange={(e) => setAssignmentForm({ ...assignmentForm, status: e.target.value as StatusOption })}>
                    <option value="">Status wählen*</option>
                    <option value="aktiv">Aktiv</option>
                    <option value="inaktiv">Inaktiv</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                {renderAssignmentOwnerSearchList(
                  filteredAssignmentOwners,
                  assignmentForm.ownerId,
                  (ownerId) => setAssignmentForm({ ...assignmentForm, ownerId })
                )}
                <small style={{ color: "var(--text-soft)" }}>{filteredAssignmentOwners.length} Treffer</small>
              </div>
              <div className="form-actions">
                <button disabled={!assignmentCanCreate || createAssignmentMutation.isPending || objectLocked} onClick={() => createAssignmentMutation.mutate()}>
                  {createAssignmentMutation.isPending ? "Speichere..." : "Zuordnung speichern"}
                </button>
              </div>
            </section>
          ) : null}

          {canManageMasterDataActions && editAssignmentForm ? (
            <section className="panel create-form resolution-create-form">
              <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1.6fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(140px, 0.7fr)", gap: 10, alignItems: "start" }}>
                <div className="form-field">
                  <label>Eigentümer*</label>
                  <input
                    placeholder="Eigentümer suchen (ID, Name, E-Mail)"
                    value={editAssignmentOwnerSearch}
                    onChange={(e) => setEditAssignmentOwnerSearch(e.target.value)}
                  />
                  <label style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      checked={editAssignmentOnlyUnassigned}
                      onChange={(e) => setEditAssignmentOnlyUnassigned(e.target.checked)}
                    />
                    Nur ohne Zuordnung
                  </label>
                </div>
                <div className="form-field">
                  <label>Objekt*</label>
                  <input value={`${summary?.object.objektnummer ?? "-"} - ${summary?.object.objektname ?? "-"}`} disabled />
                </div>
                <div className="form-field">
                  <label>Einheit*</label>
                  <select value={editAssignmentForm.unitId} onChange={(e) => setEditAssignmentForm({ ...editAssignmentForm, unitId: e.target.value })}>
                    <option value="">Einheit wählen*</option>
                    {(unitsForSelectionData?.data ?? []).map((u) => (
                      <option key={u.id} value={u.id}>{`${u.einheitsnummer} - ${u.bezeichnung || "-"}`}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Status*</label>
                  <select value={editAssignmentForm.status} onChange={(e) => setEditAssignmentForm({ ...editAssignmentForm, status: e.target.value as "aktiv" | "inaktiv" })}>
                    <option value="aktiv">Aktiv</option>
                    <option value="inaktiv">Inaktiv</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                {renderAssignmentOwnerSearchList(
                  filteredEditAssignmentOwners,
                  editAssignmentForm.ownerId,
                  (ownerId) => setEditAssignmentForm({ ...editAssignmentForm, ownerId })
                )}
                <small style={{ color: "var(--text-soft)" }}>{filteredEditAssignmentOwners.length} Treffer</small>
              </div>
              <div className="form-actions">
                <button disabled={!assignmentCanEdit || updateAssignmentMutation.isPending || objectLocked} onClick={() => updateAssignmentMutation.mutate()}>
                  {updateAssignmentMutation.isPending ? "Speichere..." : "Änderungen speichern"}
                </button>
                <button onClick={() => { setEditAssignmentForm(null); setEditAssignmentOwnerSearch(""); }}>Abbrechen</button>
              </div>
            </section>
          ) : null}

          <section className="panel resolutions-v2-list-panel object-detail-units-list-panel">
            <div className="resolutions-v2-filterbar">
              <div className="resolutions-v2-filter-fields">
                <label className="resolutions-v2-filter-input">
                  <span className="resolutions-v2-filter-icon" aria-hidden="true">{unitListIcons.search}</span>
                  <input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </label>
                <select value={assignmentRecipientFilter} onChange={(e) => setAssignmentRecipientFilter(e.target.value as AssignmentRecipientFilterOption)}>
                  <option value="">Alle</option>
                  <option value="owner">Eigentümer</option>
                  <option value="authorized">Zustellungsbevollmächtigt</option>
                </select>
              </div>
              <button type="button" className="resolutions-v2-reset-btn" onClick={() => { setSearch(""); setAssignmentRecipientFilter(""); setAssignmentPage(1); }}>
                <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">{unitListIcons.reset}</span>
                <span>Filter zurücksetzen</span>
              </button>
            </div>
            <div className="resolutions-v2-row-count">Zeilen: {assignmentsRows.length} / Gesamt: {assignmentTotalRows}</div>
            <div className="resolutions-v2-table-wrap">
              <table className="resolutions-v2-table assignments-v2-table">
                <thead>
                  <tr>
                    <th>{plainUnitHeader("Einheit-Nr", <ObjectDetailIcon name="home" />)}</th>
                    <th>
                      <span className="resolutions-v2-th-plain">
                        <span className="resolutions-v2-th-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="7.4" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                            <path d="M5.2 15.9a4.8 4.8 0 0 1 9.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                          </svg>
                        </span>
                        <span className="resolutions-v2-th-label">Eigentümer-ID</span>
                      </span>
                    </th>
                    <th>{plainUnitHeader("Eigentümername", <ObjectDetailIcon name="owners" />)}</th>
                    <th>{plainUnitHeader("E-Mail", <ObjectDetailIcon name="edit" />)}</th>
                    <th>{plainUnitHeader("Stimmrecht", unitListIcons.votes)}</th>
                    <th>{plainUnitHeader("MEA", unitListIcons.mea)}</th>
                    <th>{plainUnitHeader("Status", unitListIcons.status)}</th>
                    <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">{plainUnitHeader("", unitListIcons.actions)}</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentsRows.map((r) => (
                    <tr key={r.id}>
                      <td>{formatEinheitsnummer(r.einheitsnummer)}</td>
                      <td>{r.eigentuemer_id}</td>
                      <td><span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}><span>{ownerDisplayById.get(r.ownerId) ?? r.eigentuemername ?? "-"}</span>{r.isAuthorizedOwnerRecipient ? <span className="badge">Zustellungsbevollmächtigt</span> : null}</span></td>
                      <td>{r.eigentuemer_email || "-"}</td>
                      <td>{Number(r.einheit_stimmrecht ?? 0) > 0 ? Number(r.einheit_stimmrecht ?? 0) : "-"}</td>
                      <td>{Number(r.einheit_mea ?? 0) > 0 ? Number(r.einheit_mea ?? 0) : "-"}</td>
                      <td><span className={`badge ${r.status} quick-tooltip`} data-tooltip={buildAssignmentValidityTooltip(r)} title={buildAssignmentValidityTooltip(r)} tabIndex={0}>{r.status}</span></td>
                      <td>
                        {!canManageMasterDataActions ? <span style={{ color: "var(--text-soft)" }}>-</span> : (
                          <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="resolutions-v2-action-trigger" aria-label={`Aktionen für Zuordnung ${r.einheitsnummer}`} aria-expanded={assignmentOpenActionMenuId === r.id} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (assignmentOpenActionMenuId === r.id) { setAssignmentOpenActionMenuId(null); return; } openDetailActionMenu(r.id, e.currentTarget, setAssignmentOpenActionMenuId, 96); }}>&#8942;</button>
                            {assignmentOpenActionMenuId === r.id && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                              <div className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed" style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }} onMouseDown={(e) => e.stopPropagation()} onMouseEnter={() => setIsActionMenuHovered(true)} onMouseLeave={() => setIsActionMenuHovered(false)}>
                                <button type="button" disabled={objectLocked} onClick={() => { setEditAssignmentOwnerSearch(""); setEditAssignmentForm({ id: r.id, unitId: r.unitId, ownerId: r.ownerId, status: r.status }); setAssignmentOpenActionMenuId(null); }}>Bearbeiten</button>
                                <button type="button" disabled={objectLocked} onClick={() => { setDeleteConfirm({ id: `assignment:${r.id}`, label: `${r.einheitsnummer} / ${r.eigentuemer_id}`, code: String(Math.floor(1000 + Math.random() * 9000)), input: ["", "", "", ""], error: "" }); setAssignmentOpenActionMenuId(null); }}>Löschen</button>
                                </div>,
                                document.body
                              ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="resolutions-v2-pagination">
              <div className="resolutions-v2-page-size">
                <select value={String(assignmentPageSize)} onChange={(e) => { setAssignmentPageSize(Number(e.target.value) || 10); setAssignmentPage(1); }}>
                  <option value="10">10 pro Seite</option>
                  <option value="20">20 pro Seite</option>
                  <option value="50">50 pro Seite</option>
                </select>
              </div>
              <div className="resolutions-v2-page-center">
                <button type="button" className="resolutions-v2-page-arrow" onClick={() => setAssignmentPage((prev) => Math.max(1, prev - 1))} disabled={assignmentPage <= 1}>‹</button>
                {assignmentPageItems.map((pageItem) => <button key={pageItem} type="button" className={`resolutions-v2-page-chip ${pageItem === assignmentPage ? "active" : ""}`} onClick={() => setAssignmentPage(pageItem)}>{pageItem}</button>)}
                <button type="button" className="resolutions-v2-page-arrow" onClick={() => setAssignmentPage((prev) => Math.min(assignmentTotalPages, prev + 1))} disabled={assignmentPage >= assignmentTotalPages}>›</button>
                <span className="resolutions-v2-page-info">Seite {assignmentPage} von {assignmentTotalPages}</span>
              </div>
              <div className="resolutions-v2-page-next-wrap">
                <button type="button" className="resolutions-v2-next-btn" onClick={() => setAssignmentPage((prev) => Math.min(assignmentTotalPages, prev + 1))} disabled={assignmentPage >= assignmentTotalPages}><span>Weiter</span><span aria-hidden="true">›</span></button>
              </div>
            </div>
          </section>
        </>
      ) : null}

      {tab === "umlaufbeschluesse" ? (
        <>
          {!showCreateResolution && !editResolutionForm ? (
            <section className="page-header panel resolutions-v2-page-header">
              <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
                <span className="resolutions-v2-title-icon" aria-hidden="true"><ObjectDetailIcon name="resolution" /></span>
                <span className="resolutions-v2-title-copy">
                  <span className="resolutions-v2-title-main">Umlaufbeschlüsse</span>
                  <span className="resolutions-v2-title-sub">Übersicht aller Umlaufbeschlüsse dieses Objekts</span>
                </span>
              </h2>
              {canCreateResolutions ? (
                <button className="resolutions-v2-create-btn" onClick={() => setShowCreateResolution(true)}>
                  <span className="resolutions-v2-create-btn-icon" aria-hidden="true"><ObjectDetailIcon name="plus" /></span>
                  <span>Neuer Umlaufbeschluss</span>
                </button>
              ) : null}
            </section>
          ) : null}

          {canCreateResolutions && showCreateResolution ? (
            <section className="panel create-form resolution-create-form resolution-create-form-v2">
              <div className="resolution-create-v2-head">
                <span className="resolution-create-v2-head-icon" aria-hidden="true">
                  <ObjectDetailIcon name="resolution" />
                </span>
                <div className="resolution-create-v2-head-copy">
                  <h3>Umlaufbeschluss anlegen</h3>
                  <p>Stammdaten, Dokumente und stimmberechtigte Einheiten erfassen</p>
                </div>
              </div>
              <div className="form-grid">
                <div className="resolution-main-fields-box">
                  <div className="form-field"><label>Objekt*</label><input value={`${summary?.object.objektnummer ?? "-"} - ${summary?.object.objektname ?? "-"}`} disabled /></div>
                  <div className="form-field">
                    <label>Beschlussnummer <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>{resolutionNumberHint}</small></label>
                    <input value={createResolutionNumber} disabled />
                  </div>
                  <div className="form-field">
                    <label>Facilioo Vorgangsnummer</label>
                    <input
                      type="number"
                      min={0}
                      max={9999999999}
                      value={resolutionForm.facilioo_vorgangsnummer}
                      onChange={(e) => setResolutionForm({ ...resolutionForm, facilioo_vorgangsnummer: e.target.value })}
                      onBlur={() => {
                        void validateCreateFaciliooProcess();
                      }}
                    />
                    {createFaciliooValidationPending ? <small style={{ color: "var(--text-soft)" }}>Vorgang wird geprüft ...</small> : null}
                    {createFaciliooProcessTitle ? <small style={{ color: "var(--text-soft)" }}>Vorgangsbetreff: {createFaciliooProcessTitle}</small> : null}
                    {createFaciliooValidationError ? <small className="error-text">{createFaciliooValidationError}</small> : null}
                  </div>
                  <div className="form-field"><label>Startdatum (optional)</label><input type="date" value={resolutionForm.startdatum} onChange={(e) => setResolutionForm({ ...resolutionForm, startdatum: e.target.value })} />{resolutionDateErrors.startError ? <span className="error-text">{resolutionDateErrors.startError}</span> : null}</div>
                  <div className="form-field"><label>Enddatum (optional)</label><input type="date" value={resolutionForm.endedatum} onChange={(e) => setResolutionForm({ ...resolutionForm, endedatum: e.target.value })} />{resolutionDateErrors.endError ? <span className="error-text">{resolutionDateErrors.endError}</span> : null}</div>
                  <div className="form-field"><label>Stimmrechtsart*</label><select value={resolutionForm.stimmrechtsart} onChange={(e) => setResolutionForm({ ...resolutionForm, stimmrechtsart: e.target.value as Stimmrechtsart })}><option value="kopf">Kopfstimmen</option>{objectCanUseStimmrecht ? <option value="stimmrecht">Stimmrecht</option> : null}<option value="mea">MEA</option></select></div>
                  <div className="form-field"><label>Quorum*</label><select value={resolutionForm.quorum} onChange={(e) => { const quorum = e.target.value as QuorumArt; setResolutionForm({ ...resolutionForm, quorum, ...(quorum === "allstimmig" ? { vorbeschluss_top: "", vorbeschluss_wev_datum: "" } : {}) }); }}><option value="allstimmig">Allstimmig</option><option value="einfache_mehrheit">Einfache Mehrheit</option><option value="qualifizierte_mehrheit">Qualifizierte Mehrheit</option></select></div>
                  <div className="form-field">
                    <label>Automatischen Versandprozess aktivieren</label>
                    <div className="settings-switch-row" style={{ marginBottom: 10 }}>
                      <label className="settings-toggle" htmlFor="object-resolution-create-dispatch-enabled" aria-label="Automatischen Versandprozess aktivieren oder deaktivieren">
                        <input
                          id="object-resolution-create-dispatch-enabled"
                          type="checkbox"
                          checked={resolutionForm.dispatch_automation_enabled}
                          onChange={(e) =>
                            setResolutionForm({
                              ...resolutionForm,
                              dispatch_automation_enabled: e.target.checked
                            })
                          }
                        />
                        <span className="settings-toggle-slider" />
                      </label>
                    </div>
                  </div>
                  {isVorbeschlussRequired(resolutionForm.quorum) ? (
                    <div className="form-field">
                      <label>TOP der WEV (optional)</label>
                      <input value={resolutionForm.vorbeschluss_top} onChange={(e) => setResolutionForm({ ...resolutionForm, vorbeschluss_top: e.target.value })} />
                      <small style={{ color: "var(--text-soft)" }}>Tagesordnungspunkt des Vorbeschlusses, mit dem das abgesenkte Quorum beschlossen wurde.</small>
                    </div>
                  ) : null}
                  {isVorbeschlussRequired(resolutionForm.quorum) ? (
                    <div className="form-field">
                      <label>Datum der WEV (optional)</label>
                      <input type="date" value={resolutionForm.vorbeschluss_wev_datum} onChange={(e) => setResolutionForm({ ...resolutionForm, vorbeschluss_wev_datum: e.target.value })} />
                      <small style={{ color: "var(--text-soft)" }}>Datum der WEG-Versammlung des Vorbeschlusses.</small>
                    </div>
                  ) : null}
                  <div className="form-field form-field-wide">
                    <label>Ablauf Info's an Verwaltung senden</label>
                    <div className="settings-switch-row" style={{ marginBottom: 10 }}>
                      <label className="settings-toggle" htmlFor="object-resolution-create-trigger-enabled" aria-label="Ablauf Infos an Verwaltung senden aktivieren oder deaktivieren">
                        <input
                          id="object-resolution-create-trigger-enabled"
                          type="checkbox"
                          checked={resolutionForm.trigger_notifications_enabled}
                          onChange={(e) =>
                            setResolutionForm({
                              ...resolutionForm,
                              trigger_notifications_enabled: e.target.checked,
                              trigger_notifications_channel: e.target.checked ? resolutionForm.trigger_notifications_channel : "facilioo"
                            })
                          }
                        />
                        <span className="settings-toggle-slider" />
                      </label>
                    </div>
                    {resolutionForm.trigger_notifications_enabled ? (
                      <>
                        <div className="form-field" style={{ padding: 0, marginBottom: 10 }}>
                          <label>Versandkanal</label>
                          <select
                            value={resolutionForm.trigger_notifications_channel}
                            onChange={(e) =>
                              setResolutionForm({
                                ...resolutionForm,
                                trigger_notifications_channel: e.target.value as TriggerNotificationChannel
                              })
                            }
                          >
                            <option value="email">E-Mail</option>
                            <option value="facilioo">Facilioo Vorgang</option>
                          </select>
                        </div>
                        <div style={{ border: "1px solid var(--line)", borderRadius: 8, padding: 10 }}>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                            <button
                              type="button"
                              onClick={() =>
                                setResolutionForm({
                                  ...resolutionForm,
                                  trigger_notifications_events: allTriggerNotificationEvents()
                                })
                              }
                            >
                              Alles markieren
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setResolutionForm({
                                  ...resolutionForm,
                                  trigger_notifications_events: defaultTriggerNotificationEvents()
                                })
                              }
                            >
                              Alle demarkieren
                            </button>
                          </div>
                          {TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => (
                            <label key={eventOption.key} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                              <input
                                type="checkbox"
                                checked={Boolean(resolutionForm.trigger_notifications_events[eventOption.key])}
                                onChange={(e) =>
                                  setResolutionForm({
                                    ...resolutionForm,
                                    trigger_notifications_events: {
                                      ...resolutionForm.trigger_notifications_events,
                                      [eventOption.key]: e.target.checked
                                    }
                                  })
                                }
                              />
                              <span>{eventOption.label}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                  <div className="form-field form-field-wide"><label>Titel*</label><input value={resolutionForm.titel} onChange={(e) => setResolutionForm({ ...resolutionForm, titel: e.target.value })} /></div>
                  <div className="form-field form-field-wide"><label>Beschlusstext*</label><textarea rows={8} value={resolutionForm.beschlusstext} onChange={(e) => setResolutionForm({ ...resolutionForm, beschlusstext: e.target.value })} /></div>
                  {!resolutionVorbeschlussPairValid ? <div className="form-field form-field-wide"><span className="error-text">TOP und Datum der WEV müssen gemeinsam befüllt werden.</span></div> : null}
                </div>
                <div className="form-field form-field-wide resolution-upload-box">
                  <label>Dokumente (optional)</label>
                  <div
                    className={`resolution-document-dropzone${createResolutionDocumentsDropActive ? " is-drag-over" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "copy";
                      setCreateResolutionDocumentsDropActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setCreateResolutionDocumentsDropActive(false);
                    }}
                    onDrop={onCreateResolutionDocumentsDrop}
                  >
                    <input
                      ref={createResolutionDocumentsInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png"
                      style={{ display: "none" }}
                      onChange={(e) => mergeCreateResolutionDocuments(Array.from(e.target.files ?? []), true)}
                    />
                    <div className="resolution-document-dropzone-text">Dateien hier hineinziehen oder über den Button auswählen.</div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
                    <button type="button" onClick={() => createResolutionDocumentsInputRef.current?.click()}>
                      Dateien auswählen
                    </button>
                    <button type="button" onClick={() => setCreateResolutionDocuments([])} disabled={!createResolutionDocuments.length}>
                      Auswahl leeren
                    </button>
                    <span style={{ color: "var(--text-soft)", fontSize: 12 }}>
                      {createResolutionDocuments.length ? `${createResolutionDocuments.length} Datei(en) in der Liste` : "Keine Dateien ausgewählt"}
                    </span>
                  </div>
                  <small style={{ color: "var(--text-soft)" }}>Erlaubte Formate: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG. Maximal 20 Dateien pro Upload, maximal 20 MB je Datei.</small>
                </div>
                {renderUnitSelector(
                  unitSelectionRows,
                  resolutionForm.stimmberechtigte_einheiten,
                  resolutionOwnerSearch,
                  setResolutionOwnerSearch,
                  resolutionUnitTypeFilter,
                  setResolutionUnitTypeFilter,
                  resolutionEntranceFilter,
                  setResolutionEntranceFilter,
                  (unitId, checked) => setResolutionForm((prev) => ({ ...prev, stimmberechtigte_einheiten: checked ? Array.from(new Set([...prev.stimmberechtigte_einheiten, unitId])) : prev.stimmberechtigte_einheiten.filter((v) => v !== unitId) })),
                  () => setResolutionForm((prev) => ({ ...prev, stimmberechtigte_einheiten: unitSelectionRows.map((u) => u.unitId) })),
                  () => setResolutionForm((prev) => ({ ...prev, stimmberechtigte_einheiten: [] })),
                  () => {
                    const filteredIds = getFilteredResolutionUnitIds(unitSelectionRows, resolutionOwnerSearch, resolutionUnitTypeFilter, resolutionEntranceFilter);
                    setResolutionForm((prev) => ({ ...prev, stimmberechtigte_einheiten: Array.from(new Set([...prev.stimmberechtigte_einheiten, ...filteredIds])) }));
                  },
                  () => {
                    const filteredIds = getFilteredResolutionUnitIds(unitSelectionRows, resolutionOwnerSearch, resolutionUnitTypeFilter, resolutionEntranceFilter);
                    setResolutionForm((prev) => ({ ...prev, stimmberechtigte_einheiten: prev.stimmberechtigte_einheiten.filter((x) => !filteredIds.includes(x)) }));
                  }
                )}
              </div>
              <div className="form-actions resolution-create-actions"><button className="resolution-primary-btn" disabled={!resolutionCanCreate || createResolutionMutation.isPending} onClick={() => createResolutionMutation.mutate()}>{createResolutionMutation.isPending ? "Speichere..." : "Umlaufbeschluss speichern"}</button><button type="button" className="resolution-secondary-btn" onClick={() => { setResolutionForm({ objectId: id, beschlussnummer: "", titel: "", startdatum: "", endedatum: "", stimmrechtsart: resolveDefaultStimmrechtsart(objectStandardStimmrechtsart, objectHasStimmrechte), quorum: "allstimmig", vorbeschluss_top: "", vorbeschluss_wev_datum: "", beschlusstext: "", facilioo_vorgangsnummer: "", dispatch_automation_enabled: false, trigger_notifications_enabled: false, trigger_notifications_channel: "facilioo", trigger_notifications_events: defaultTriggerNotificationEvents(), stimmberechtigte_einheiten: unitSelectionRows.map((u) => u.unitId) }); setResolutionOwnerSearch(""); setResolutionUnitTypeFilter(""); setResolutionEntranceFilter(""); setCreateResolutionDocuments([]); setCreateFaciliooValidationError(""); setCreateFaciliooProcessTitle(""); setFormError(""); setShowCreateResolution(false); }}>Abbrechen</button></div>
            </section>
          ) : null}

          {!showCreateResolution && canManageMasterDataActions && editResolutionForm ? (
            <section className="panel create-form resolution-create-form resolution-create-form-v2">
              <div className="resolution-create-v2-head">
                <span className="resolution-create-v2-head-icon" aria-hidden="true">
                  <ObjectDetailIcon name="resolution" />
                </span>
                <div className="resolution-create-v2-head-copy">
                  <h3>Umlaufbeschluss bearbeiten</h3>
                  <p>Stammdaten, Dokumente und stimmberechtigte Einheiten bearbeiten</p>
                </div>
              </div>
              <div className="form-grid">
                <div className="resolution-main-fields-box">
                <div className="form-field"><label>Objekt*</label><input value={`${summary?.object.objektnummer ?? "-"} - ${summary?.object.objektname ?? "-"}`} disabled /></div>
                <div className="form-field">
                  <label>Beschlussnummer <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>{resolutionNumberHint}</small></label>
                  <input value={editResolutionForm.beschlussnummer} disabled />
                </div>
                <div className="form-field">
                  <label>Facilioo Vorgangsnummer</label>
                  <input
                    type="number"
                    min={0}
                    max={9999999999}
                    value={editResolutionForm.facilioo_vorgangsnummer}
                    onChange={(e) => setEditResolutionForm({ ...editResolutionForm, facilioo_vorgangsnummer: e.target.value })}
                    onBlur={() => {
                      void validateEditFaciliooProcess();
                    }}
                  />
                  {editFaciliooValidationPending ? <small style={{ color: "var(--text-soft)" }}>Vorgang wird geprüft ...</small> : null}
                  {editFaciliooProcessTitle ? <small style={{ color: "var(--text-soft)" }}>Vorgangsbetreff: {editFaciliooProcessTitle}</small> : null}
                  {editFaciliooValidationError ? <small className="error-text">{editFaciliooValidationError}</small> : null}
                </div>
                <div className="form-field"><label>Startdatum</label><input type="date" value={editResolutionForm.startdatum} onChange={(e) => setEditResolutionForm({ ...editResolutionForm, startdatum: e.target.value })} />{editResolutionDateErrors.startError ? <span className="error-text">{editResolutionDateErrors.startError}</span> : null}</div>
                <div className="form-field"><label>Enddatum</label><input type="date" value={editResolutionForm.endedatum} onChange={(e) => setEditResolutionForm({ ...editResolutionForm, endedatum: e.target.value })} />{editResolutionDateErrors.endError ? <span className="error-text">{editResolutionDateErrors.endError}</span> : null}</div>
                <div className="form-field"><label>Stimmrechtsart*</label><select value={editResolutionForm.stimmrechtsart} onChange={(e) => setEditResolutionForm({ ...editResolutionForm, stimmrechtsart: e.target.value as Stimmrechtsart })}><option value="kopf">Kopfstimmen</option>{objectCanUseStimmrecht ? <option value="stimmrecht">Stimmrecht</option> : null}<option value="mea">MEA</option></select></div>
                <div className="form-field"><label>Quorum*</label><select value={editResolutionForm.quorum} onChange={(e) => { const quorum = e.target.value as QuorumArt; setEditResolutionForm({ ...editResolutionForm, quorum, ...(quorum === "allstimmig" ? { vorbeschluss_top: "", vorbeschluss_wev_datum: "" } : {}) }); }}><option value="allstimmig">Allstimmig</option><option value="einfache_mehrheit">Einfache Mehrheit</option><option value="qualifizierte_mehrheit">Qualifizierte Mehrheit</option></select></div>
                <div className="form-field">
                  <label>Automatischen Versandprozess aktivieren</label>
                  <div className="settings-switch-row" style={{ marginBottom: 10 }}>
                    <label className="settings-toggle" htmlFor="object-resolution-edit-dispatch-enabled" aria-label="Automatischen Versandprozess aktivieren oder deaktivieren">
                      <input
                        id="object-resolution-edit-dispatch-enabled"
                        type="checkbox"
                        checked={editResolutionForm.dispatch_automation_enabled}
                        onChange={(e) =>
                          setEditResolutionForm({
                            ...editResolutionForm,
                            dispatch_automation_enabled: e.target.checked
                          })
                        }
                      />
                      <span className="settings-toggle-slider" />
                    </label>
                  </div>
                </div>
                {isVorbeschlussRequired(editResolutionForm.quorum) ? (
                  <div className="form-field">
                    <label>TOP der WEV (optional)</label>
                    <input value={editResolutionForm.vorbeschluss_top} onChange={(e) => setEditResolutionForm({ ...editResolutionForm, vorbeschluss_top: e.target.value })} />
                    <small style={{ color: "var(--text-soft)" }}>Tagesordnungspunkt des Vorbeschlusses, mit dem das abgesenkte Quorum beschlossen wurde.</small>
                  </div>
                ) : null}
                {isVorbeschlussRequired(editResolutionForm.quorum) ? (
                  <div className="form-field">
                    <label>Datum der WEV (optional)</label>
                    <input type="date" value={editResolutionForm.vorbeschluss_wev_datum} onChange={(e) => setEditResolutionForm({ ...editResolutionForm, vorbeschluss_wev_datum: e.target.value })} />
                    <small style={{ color: "var(--text-soft)" }}>Datum der WEG-Versammlung des Vorbeschlusses.</small>
                  </div>
                ) : null}
                <div className="form-field form-field-wide">
                  <label>Ablauf Info's an Verwaltung senden</label>
                  <div className="settings-switch-row" style={{ marginBottom: 10 }}>
                    <label className="settings-toggle" htmlFor="object-resolution-edit-trigger-enabled" aria-label="Ablauf Infos an Verwaltung senden aktivieren oder deaktivieren">
                      <input
                        id="object-resolution-edit-trigger-enabled"
                        type="checkbox"
                        checked={editResolutionForm.trigger_notifications_enabled}
                        onChange={(e) =>
                          setEditResolutionForm({
                            ...editResolutionForm,
                            trigger_notifications_enabled: e.target.checked,
                            trigger_notifications_channel: e.target.checked ? editResolutionForm.trigger_notifications_channel : "facilioo"
                          })
                        }
                      />
                      <span className="settings-toggle-slider" />
                    </label>
                  </div>
                  {editResolutionForm.trigger_notifications_enabled ? (
                    <>
                      <div className="form-field" style={{ padding: 0, marginBottom: 10 }}>
                        <label>Versandkanal</label>
                        <select
                          value={editResolutionForm.trigger_notifications_channel}
                          onChange={(e) =>
                            setEditResolutionForm({
                              ...editResolutionForm,
                              trigger_notifications_channel: e.target.value as TriggerNotificationChannel
                            })
                          }
                        >
                          <option value="email">E-Mail</option>
                          <option value="facilioo">Facilioo Vorgang</option>
                        </select>
                      </div>
                      <div className="resolution-trigger-events-box">
                        <div className="resolution-trigger-events-actions">
                          <button
                            type="button"
                            onClick={() =>
                              setEditResolutionForm({
                                ...editResolutionForm,
                                trigger_notifications_events: allTriggerNotificationEvents()
                              })
                            }
                          >
                            Alles markieren
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setEditResolutionForm({
                                ...editResolutionForm,
                                trigger_notifications_events: defaultTriggerNotificationEvents()
                              })
                            }
                          >
                            Alle demarkieren
                          </button>
                        </div>
                        <div className="resolution-trigger-events-grid">
                        {TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => (
                          <label key={eventOption.key} className="resolution-trigger-event-option">
                            <input
                              type="checkbox"
                              checked={Boolean(editResolutionForm.trigger_notifications_events[eventOption.key])}
                              onChange={(e) =>
                                setEditResolutionForm({
                                  ...editResolutionForm,
                                  trigger_notifications_events: {
                                    ...editResolutionForm.trigger_notifications_events,
                                    [eventOption.key]: e.target.checked
                                  }
                                })
                              }
                            />
                            <span>{eventOption.label}</span>
                          </label>
                        ))}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
                <div className="form-field form-field-wide"><label>Titel*</label><input value={editResolutionForm.titel} onChange={(e) => setEditResolutionForm({ ...editResolutionForm, titel: e.target.value })} /></div>
                <div className="form-field form-field-wide"><label>Beschlusstext*</label><textarea rows={8} value={editResolutionForm.beschlusstext} onChange={(e) => setEditResolutionForm({ ...editResolutionForm, beschlusstext: e.target.value })} /></div>
                {!editResolutionVorbeschlussPairValid ? <div className="form-field form-field-wide"><span className="error-text">TOP und Datum der WEV müssen gemeinsam befüllt werden.</span></div> : null}
                </div>
                {renderUnitSelector(
                  unitSelectionRows,
                  editResolutionForm.stimmberechtigte_einheiten,
                  editResolutionOwnerSearch,
                  setEditResolutionOwnerSearch,
                  editResolutionUnitTypeFilter,
                  setEditResolutionUnitTypeFilter,
                  editResolutionEntranceFilter,
                  setEditResolutionEntranceFilter,
                  (unitId, checked) => setEditResolutionForm({ ...editResolutionForm, stimmberechtigte_einheiten: checked ? Array.from(new Set([...editResolutionForm.stimmberechtigte_einheiten, unitId])) : editResolutionForm.stimmberechtigte_einheiten.filter((v) => v !== unitId) }),
                  () => setEditResolutionForm({ ...editResolutionForm, stimmberechtigte_einheiten: unitSelectionRows.map((u) => u.unitId) }),
                  () => setEditResolutionForm({ ...editResolutionForm, stimmberechtigte_einheiten: [] }),
                  () => {
                    const filteredIds = getFilteredResolutionUnitIds(unitSelectionRows, editResolutionOwnerSearch, editResolutionUnitTypeFilter, editResolutionEntranceFilter);
                    setEditResolutionForm({ ...editResolutionForm, stimmberechtigte_einheiten: Array.from(new Set([...editResolutionForm.stimmberechtigte_einheiten, ...filteredIds])) });
                  },
                  () => {
                    const filteredIds = getFilteredResolutionUnitIds(unitSelectionRows, editResolutionOwnerSearch, editResolutionUnitTypeFilter, editResolutionEntranceFilter);
                    setEditResolutionForm({ ...editResolutionForm, stimmberechtigte_einheiten: editResolutionForm.stimmberechtigte_einheiten.filter((x) => !filteredIds.includes(x)) });
                  }
                )}
              </div>
              <div className="form-actions resolution-create-actions">
                <button className="resolution-primary-btn" disabled={!resolutionCanEdit || updateResolutionMutation.isPending} onClick={() => updateResolutionMutation.mutate()}>{updateResolutionMutation.isPending ? "Speichere..." : "Änderungen speichern"}</button>
                <button type="button" className="resolution-secondary-btn" onClick={() => { setEditResolutionForm(null); setEditResolutionOwnerSearch(""); setEditResolutionUnitTypeFilter(""); setEditResolutionEntranceFilter(""); setEditFaciliooValidationError(""); setEditFaciliooProcessTitle(""); }}>Abbrechen</button>
              </div>
            </section>
          ) : null}

          {!showCreateResolution ? (
            <>
              <section className="panel resolutions-v2-list-panel object-detail-units-list-panel">
                <div className="resolutions-v2-filterbar">
                  <div className="resolutions-v2-filter-fields">
                    <label className="resolutions-v2-filter-input">
                      <span className="resolutions-v2-filter-icon" aria-hidden="true">{unitListIcons.search}</span>
                      <input placeholder="Suche..." value={resolutionSearch} onChange={(e) => setResolutionSearch(e.target.value)} />
                    </label>
                    <select value={resolutionStatusFilter} onChange={(e) => setResolutionStatusFilter(e.target.value as ResolutionStatusFilter)}>
                      <option value="">Alle Status</option>
                      <option value="entwurf">Entwurf</option>
                      <option value="aktiv">Läuft</option>
                      <option value="abgeschlossen">Beendet</option>
                    </select>
                    <select value={resolutionResultFilter} onChange={(e) => setResolutionResultFilter(e.target.value as ResolutionResultFilter)}>
                      <option value="">Alle Ergebnisse</option>
                      <option value="angenommen">Angenommen</option>
                      <option value="abgelehnt">Abgelehnt</option>
                      <option value="offen">Offen</option>
                    </select>
                  </div>
                  <button type="button" className="resolutions-v2-reset-btn" onClick={() => { setResolutionSearch(""); setResolutionStatusFilter(""); setResolutionResultFilter(""); setResolutionSortBy("beschlussnummer"); setResolutionSortDir("asc"); setResolutionPage(1); }}>
                    <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">{unitListIcons.reset}</span>
                    <span>Filter zurücksetzen</span>
                  </button>
                </div>
                <div className="resolutions-v2-row-count">Zeilen: {resolutionsRows.length} / Gesamt: {resolutionTotalRows}</div>
                <div className="resolutions-v2-table-wrap">
                  <table className="resolutions-v2-table object-detail-resolutions-v2-table">
                    <colgroup>
                      <col className="resolutions-v2-col-id" />
                      <col className="resolutions-v2-col-title" />
                      <col className="resolutions-v2-col-status" />
                      <col className="resolutions-v2-col-runtime" />
                      <col className="resolutions-v2-col-remaining" />
                      <col className="resolutions-v2-col-response" />
                      <col className="resolutions-v2-col-result" />
                      <col className="resolutions-v2-col-actions" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>
                          <button type="button" className="resolutions-v2-th-sort" onClick={() => { setResolutionSortBy("beschlussnummer"); setResolutionSortDir((prev) => resolutionSortBy === "beschlussnummer" && prev === "asc" ? "desc" : "asc"); }}>
                            <span className="resolutions-v2-th-icon" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none">
                                <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                                <path d="M11.7 3.9v2.8h2.8M6.7 9.2h5.9M6.7 12h5.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                              </svg>
                            </span>
                            <span className="resolutions-v2-th-label">Beschluss-ID</span>
                          </button>
                        </th>
                        <th>
                          <button type="button" className="resolutions-v2-th-sort" onClick={() => { setResolutionSortBy("titel"); setResolutionSortDir((prev) => resolutionSortBy === "titel" && prev === "asc" ? "desc" : "asc"); }}>
                            <span className="resolutions-v2-th-icon" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none">
                                <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                                <path d="M6.8 9.4h6.1M6.8 12.2h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                              </svg>
                            </span>
                            <span className="resolutions-v2-th-label">Titel</span>
                          </button>
                        </th>
                        <th>
                          <button type="button" className="resolutions-v2-th-sort" onClick={() => { setResolutionSortBy("status"); setResolutionSortDir((prev) => resolutionSortBy === "status" && prev === "asc" ? "desc" : "asc"); }}>
                            <span className="resolutions-v2-th-icon" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
                                <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
                              </svg>
                            </span>
                            <span className="resolutions-v2-th-label">Status</span>
                          </button>
                        </th>
                        <th>
                          <span className="resolutions-v2-th-plain">
                            <span className="resolutions-v2-th-icon" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none">
                                <rect x="4.3" y="5.6" width="11.4" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
                                <path d="M6.8 3.8v2.4M13.2 3.8v2.4M4.3 8.8h11.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                              </svg>
                            </span>
                            <span className="resolutions-v2-th-label">Laufzeit</span>
                          </span>
                        </th>
                        <th>
                          <span className="resolutions-v2-th-plain">
                            <span className="resolutions-v2-th-icon" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="11" r="5.8" stroke="currentColor" strokeWidth="1.7" />
                                <path d="M10 11V7.8M10 11l2.5 1.8M8 3.8h4M9 3.8v1.8M11 3.8v1.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                            <span className="resolutions-v2-th-label">Restlaufzeit</span>
                          </span>
                        </th>
                        <th>
                          <button type="button" className="resolutions-v2-th-sort" onClick={() => { setResolutionSortBy("ruecklaufquote"); setResolutionSortDir((prev) => resolutionSortBy === "ruecklaufquote" && prev === "asc" ? "desc" : "asc"); }}>
                            <span className="resolutions-v2-th-icon" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none">
                                <path d="M15.2 7.2a5.8 5.8 0 1 0 .4 6.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                <path d="m14 4.7 2.3 2.5-2.5 2.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                            <span className="resolutions-v2-th-label">Rücklaufquote</span>
                          </button>
                        </th>
                        <th>
                          <span className="resolutions-v2-th-plain">
                            <span className="resolutions-v2-th-icon" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none">
                                <path d="M6.2 4.8h7.6v1.3a3.8 3.8 0 0 1-7.6 0V4.8Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                                <path d="M8.2 10.2h3.6M10 10.2v2.6M7.6 15h4.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                <path d="M6.2 5.6H4.6A1.8 1.8 0 0 0 2.8 7.4v.2a2.6 2.6 0 0 0 2.6 2.6h.5M13.8 5.6h1.6a1.8 1.8 0 0 1 1.8 1.8v.2a2.6 2.6 0 0 1-2.6 2.6h-.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                            <span className="resolutions-v2-th-label">Ergebnis</span>
                          </span>
                        </th>
                        <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">{plainUnitHeader("", unitListIcons.actions)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resolutionsRows.map((r) => {
                        const isActive = r.status === "aktiv";
                        const canEdit = r.status === "entwurf";
                        const hasVotes = (r.abgegebene_stimmen ?? 0) > 0;
                        const outcome = getResolutionOutcome(r);
                        const remainingDays = getRemainingDays(r.endedatum);
                        const totalRecipients = Number(r.anzahl_empfaenger_gesamt ?? 0);
                        const submittedVotes = Number(r.abgegebene_stimmen ?? 0);
                        const responseRate = totalRecipients > 0
                          ? Math.max(0, Math.min(100, (submittedVotes / totalRecipients) * 100))
                          : Math.max(0, Math.min(100, Number(r.ruecklaufquote ?? 0)));
                        return (
                          <tr key={r.id}>
                            <td><Link className="badge resolution-number" to={`/resolutions/${r.id}`} title={r.beschlussnummer}>{r.beschlussnummer}</Link></td>
                            <td className="resolutions-v2-title-cell">{r.titel}</td>
                            <td><span className={`badge ${r.status}`}>{formatResolutionStatus(r.status)}</span></td>
                            <td>
                              <div className="resolutions-v2-date-range">
                                <span className="resolutions-v2-date-item">
                                  <span className="resolutions-v2-date-item-icon" aria-hidden="true">
                                    <svg viewBox="0 0 16 16" fill="none">
                                      <path d="M3.4 3.6h9.2v8.8H3.4z" stroke="currentColor" strokeWidth="1.4" />
                                      <path d="M5.2 2.5v2.1M10.8 2.5v2.1M3.4 6h9.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                  </span>
                                  <span>{formatDateDE(r.startdatum)}</span>
                                </span>
                                <span className="resolutions-v2-date-item">
                                  <span className="resolutions-v2-date-item-icon" aria-hidden="true">
                                    <svg viewBox="0 0 16 16" fill="none">
                                      <path d="M3.4 3.6h9.2v8.8H3.4z" stroke="currentColor" strokeWidth="1.4" />
                                      <path d="M5.2 2.5v2.1M10.8 2.5v2.1M3.4 6h9.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                      <path d="m5.6 9 1.4 1.4 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                  <span>{formatDateDE(r.endedatum)}</span>
                                </span>
                              </div>
                            </td>
                            <td>
                              {remainingDays === null ? "-" : (
                                <span className="resolutions-v2-remaining-bullet" title={`${remainingDays} Tage`}>
                                  {remainingDays}
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="resolutions-v2-progress-wrap">
                                <span>{responseRate.toFixed(2)} %</span>
                                <span className="resolutions-v2-progress-track" aria-hidden="true">
                                  <span className="resolutions-v2-progress-fill" style={{ width: `${responseRate}%` }} />
                                </span>
                              </div>
                            </td>
                            <td>{outcome.tone ? <span className={`badge ergebnis-${outcome.tone === "success" ? "angenommen" : "abgelehnt"}`}>{outcome.label}</span> : <span className="badge ergebnis-neutral">{outcome.label}</span>}</td>
                            <td>
                              {!canManageMasterDataActions ? <span style={{ color: "var(--text-soft)" }}>-</span> : (
                                <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                                  <button type="button" className="resolutions-v2-action-trigger" aria-label={`Aktionen für Beschluss ${r.beschlussnummer}`} aria-expanded={resolutionOpenActionMenuId === r.id} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (resolutionOpenActionMenuId === r.id) { setResolutionOpenActionMenuId(null); return; } openDetailActionMenu(r.id, e.currentTarget, setResolutionOpenActionMenuId, 188); }}>&#8942;</button>
                                  {resolutionOpenActionMenuId === r.id && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                                    <div className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed" style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }} onMouseDown={(e) => e.stopPropagation()} onMouseEnter={() => setIsActionMenuHovered(true)} onMouseLeave={() => setIsActionMenuHovered(false)}>
                                      <button type="button" disabled={!canEdit} onClick={() => { if (!canEdit) { setActionError("Nur Entwürfe können bearbeitet werden. Laufende oder beendete Beschlüsse sind gesperrt."); return; } setEditFaciliooValidationError(""); setEditFaciliooProcessTitle(""); setEditResolutionForm({ id: r.id, objectId: r.objectId, beschlussnummer: r.beschlussnummer, titel: r.titel, startdatum: r.startdatum ?? "", endedatum: r.endedatum ?? "", stimmrechtsart: r.stimmrechtsart, quorum: r.quorum, vorbeschluss_top: r.vorbeschluss_top ?? "", vorbeschluss_wev_datum: r.vorbeschluss_wev_datum ?? "", beschlusstext: r.beschlusstext, facilioo_vorgangsnummer: r.facilioo_vorgangsnummer ? String(r.facilioo_vorgangsnummer) : "", dispatch_automation_enabled: r.dispatch_automation_enabled ?? false, trigger_notifications_enabled: r.trigger_notifications_enabled ?? false, trigger_notifications_channel: r.trigger_notifications_channel ?? "facilioo", trigger_notifications_events: normalizeTriggerNotificationEvents(r.trigger_notifications_events), stimmberechtigte_einheiten: r.stimmberechtigte_einheiten ?? [] }); setResolutionOpenActionMenuId(null); }}>Bearbeiten</button>
                                      {r.status === "entwurf" ? <button type="button" onClick={() => { startResolutionMutation.mutate(r.id); setResolutionOpenActionMenuId(null); }} disabled={startResolutionMutation.isPending}>Starten</button> : null}
                                      {r.status === "aktiv" ? <button type="button" onClick={() => { setStopConfirm({ id: r.id, label: r.beschlussnummer, code: String(Math.floor(1000 + Math.random() * 9000)), input: ["", "", "", ""], error: "" }); setResolutionOpenActionMenuId(null); }} disabled={stopResolutionMutation.isPending}>Stoppen</button> : null}
                                      <button type="button" disabled={isActive || hasVotes} onClick={() => { if (isActive) { setActionError("Aktive Beschlüsse können nicht gelöscht werden."); return; } if (hasVotes) { setActionError("Beschlüsse mit Stimmabgaben können nicht gelöscht werden."); return; } setDeleteConfirm({ id: `resolution:${r.id}`, label: r.beschlussnummer, code: String(Math.floor(1000 + Math.random() * 9000)), input: ["", "", "", ""], error: "" }); setResolutionOpenActionMenuId(null); }}>Löschen</button>
                                    </div>,
                                    document.body
                                  ) : null}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="resolutions-v2-pagination">
                  <div className="resolutions-v2-page-size">
                    <select value={String(resolutionPageSize)} onChange={(e) => { setResolutionPageSize(Number(e.target.value) || 10); setResolutionPage(1); }}>
                      <option value="10">10 pro Seite</option>
                      <option value="20">20 pro Seite</option>
                      <option value="50">50 pro Seite</option>
                    </select>
                  </div>
                  <div className="resolutions-v2-page-center">
                    <button type="button" className="resolutions-v2-page-arrow" onClick={() => setResolutionPage((prev) => Math.max(1, prev - 1))} disabled={resolutionPage <= 1}>‹</button>
                    {resolutionPageItems.map((pageItem) => <button key={pageItem} type="button" className={`resolutions-v2-page-chip ${pageItem === resolutionPage ? "active" : ""}`} onClick={() => setResolutionPage(pageItem)}>{pageItem}</button>)}
                    <button type="button" className="resolutions-v2-page-arrow" onClick={() => setResolutionPage((prev) => Math.min(resolutionTotalPages, prev + 1))} disabled={resolutionPage >= resolutionTotalPages}>›</button>
                    <span className="resolutions-v2-page-info">Seite {resolutionPage} von {resolutionTotalPages}</span>
                  </div>
                  <div className="resolutions-v2-page-next-wrap">
                    <button type="button" className="resolutions-v2-next-btn" onClick={() => setResolutionPage((prev) => Math.min(resolutionTotalPages, prev + 1))} disabled={resolutionPage >= resolutionTotalPages}><span>Weiter</span><span aria-hidden="true">›</span></button>
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </>
      ) : null}

      {tab === "importe" ? <ServerDataTable columns={[{ key: "import_typ", label: "Importtyp" }, { key: "filename", label: "Datei" }, { key: "status", label: "Status" }, { key: "total_rows", label: "Gesamtzeilen" }, { key: "valid_rows", label: "Valide Zeilen" }, { key: "invalid_rows", label: "Fehlerzeilen" }, { key: "createdAt", label: "Zeitpunkt" }]} rows={importsData?.data ?? []} total={importsData?.meta.total ?? 0} page={importPage} pageSize={25} onPageChange={setImportPage} /> : null}
      {tab === "protokoll" ? <ServerDataTable columns={[{ key: "createdAt", label: "Zeitpunkt" }, { key: "actor_name", label: "Benutzer" }, { key: "action", label: "Aktion" }, { key: "entity_type", label: "Datensatztyp" }, { key: "entity_id", label: "Datensatz-ID" }, { key: "object_ref", label: "Objektbezug" }]} rows={auditData?.data ?? []} total={auditData?.meta.total ?? 0} page={auditPage} pageSize={25} onPageChange={setAuditPage} /> : null}

      {formError ? <section className="panel error-text">{formError}</section> : null}
      {actionError ? <section className="panel error-text">{actionError}</section> : null}
      {summaryError ? <section className="panel error-text">Objektdetailseite konnte nicht geladen werden.</section> : null}

      <DeleteCodeConfirmModal
        open={!!deleteConfirm}
        message={deleteConfirm ? `${deleteConfirm.label} soll gelöscht werden.` : ""}
        code={deleteConfirm?.code ?? ""}
        input={deleteConfirm?.input ?? ["", "", "", ""]}
        error={deleteConfirm?.error}
        isPending={false}
        onClose={() => setDeleteConfirm(null)}
        onInputChange={(next) => {
          if (!deleteConfirm) return;
          setDeleteConfirm({ ...deleteConfirm, input: next });
        }}
        onErrorReset={() => {
          if (!deleteConfirm || !deleteConfirm.error) return;
          setDeleteConfirm({ ...deleteConfirm, error: "" });
        }}
        onConfirm={() => {
          if (!deleteConfirm) return;
          if (deleteConfirm.input.join("") !== deleteConfirm.code) {
            setDeleteConfirm({ ...deleteConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          void resolveDelete();
        }}
      />

      <DeleteCodeConfirmModal
        open={!!stopConfirm}
        title="Beschluss stoppen"
        message={stopConfirm ? `Beschluss ${stopConfirm.label} soll manuell gestoppt werden.` : ""}
        code={stopConfirm?.code ?? ""}
        input={stopConfirm?.input ?? ["", "", "", ""]}
        error={stopConfirm?.error}
        isPending={stopResolutionMutation.isPending}
        onClose={() => setStopConfirm(null)}
        onInputChange={(next) => {
          if (!stopConfirm) return;
          setStopConfirm({ ...stopConfirm, input: next });
        }}
        onErrorReset={() => {
          if (!stopConfirm || !stopConfirm.error) return;
          setStopConfirm({ ...stopConfirm, error: "" });
        }}
        confirmLabel="Stoppen bestätigen"
        pendingLabel="Stoppe..."
        onConfirm={() => {
          if (!stopConfirm) return;
          if (stopConfirm.input.join("") !== stopConfirm.code) {
            setStopConfirm({ ...stopConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          stopResolutionMutation.mutate(stopConfirm.id);
        }}
      />
    </div>
  );
};

const getRemainingDays = (endDate?: string) => {
  if (!endDate) return null;
  const match = endDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  const end = new Date(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw));
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((end.getTime() - today.getTime()) / millisecondsPerDay) + 1;
  return Math.max(0, diffDays);
};




















































